/*!
 * cueDispatcher.js — runs cues, registers them, and fires them at the playhead
 * © 2025 Rob Canning — GPLv3
 *
 *  - handleCueTrigger(expr | ast): parse a cue and hand it to its handler
 *    (the switch; the score's own cues -- pause, nav, page, seek ... -- are
 *    looked up in scoreCues.js)
 *  - assignCues / registerSingleCue / unregisterSingleCue: which score
 *    elements are cues (window.cues), with their overlays, waveforms and
 *    click gestures
 *  - checkCueTriggers: each frame, which cues the playhead has reached or
 *    left, and the resets after a jump (resetCueState,
 *    resetCueEdgeTracking)
 *  - notation rendering (renderNotationCue), DSL extent lines,
 *    scheduleCueStart (tdelay:)
 */

import { parseCueToAST, parseUseDirective } from "../parser/parser.js";
import { CUE_START_RE } from "../parser/cueNames.js";
import { evalMaybeRandom, makeEvery } from "./audio/patterns.js";
import { isObjectTrig, registerProbeTarget } from "./probeTriggers.js";
import { isSignalTrig, registerSignalTrigger, dropSignalTrigger }
  from "../control/signalTriggers.js";
import { splitCueId, isAuthoredExtentGroup, expandCluster, truthy, isOff, readChance, readEvery, stepsItself } from "../parser/cueUtils.js";
import { handleTempoCue } from "./tempo.js";

import { handleStopwatchCue, dismissAllStopwatchOverlays } from "./timers.js";
import { handleAdcCue } from "./adc.js";
import { handleRandomCue } from "./random.js";
import { handleFadeCueFromAST, primeFadeTargetFromAST, forgetFadeTarget, resetAllFadePriming } from "./fade.js";
import { handleVideoCueFromAST } from "./video.js";
import { buildCueButtonsIn, buildClickOverlaysIn, buildDragClickOverlaysIn, buildDragOverlaysIn, repositionAllClickOverlays, rebuildOverlaysFor, destroyOverlaysFor } from "./button.js";
import { handleMetronomeCue, stopMetro } from "./metro.js";
import { handleRotateCue } from "./rotate.js";
import { later as sequenceLater, cancelLater as cancelSequenceLater } from "./sequence.js";
import { handleScaleCue } from "./scale.js";
import { handleO2PCue, releaseO2P } from "./o2p.js";
import { handleColorCue } from "./color.js";
import { appCue } from "./appCues.js";
import {
  handleAudioCue, handleHushCue,
  handleAudioPoolCue, handleAudioImpulseCue, stopAudioImpulse,
  primeAudioOverlay, primeAudioPoolOverlay, primeAudioImpulseOverlay,
  primeWaveform, primeImpulseWaveform, primePoolWaveform
} from "./audio/index.js";

import { handleSynthCue, primeSynthOverlay, stopSynth, learnSynthLine, WAITS } from "./synth.js";
import { handleLevelCue, handleXfadeCue, soloOwned } from "./audio/level.js";
import { playheadScreenX } from "../system/paths.js";
import { getSelectedClients } from "../system/room.js";
import { parseMidiSpec } from "../control/midiSpec.js";
import { midiTrigSignal, wantMidiIn } from "../control/midiIn.js";
import { handleTransCue } from "./trans.js";
import { handleShapeCue } from "./shape.js";
import { handleUseLive } from "./useLive.js";
import { handleVizCue, handlePaletteCue } from "./viz.js";
// at once, not on first use: the viz(..., cell:) lines after grid() need its grid
import { handleGridCue } from "./grid.js";
import { patternStepper } from "./patternSteps.js";

/** Animations a cluster's members each take in place (STEP 3.45), not the cluster as one. */
const CLUSTER_IN_PLACE = new Set(["cueRotate", "cueScale", "cueColor", "cueFade"]);
import { destroyWaveform, getWaveformsByPrefix } from "../system/waveform.js";

import "../parser/preProcessPropagate.js";
import { registerPin, registerPinRelease } from "../system/cuePin.js";
import { elapsedAtPlayhead } from "../system/scoreTime.js";
import { noteScene } from "../system/sceneReadout.js";
import { resetAnimationsForElement, isElementAnimating, stopAnimationsOn } from "./animation.js";
import { beginRefScan, noteSignalRef, reportUnresolvedRefs, setPortsResolver, dropRefsFor }
  from "../control/refs.js";
import { portsFor as _portsForCue } from "../control/ports.js";
// refs.js checks a binding's TARGET against the port table, so a follow()
// aimed at a parameter the cue does not read is reported instead of
// silently doing nothing. Injected rather than imported there so refs.js
// stays loadable on its own in tests, the same as setUidResolver.
setPortsResolver(_portsForCue);
import { beginEdgeScan, addSignalEdge, addTriggerEdge, addSignalTriggerEdge, dropEdgesFor } from "../control/edges.js";
import { handleSpeedCue, handleSpeedRamp, handleSpeedRampOverElement } from "./speed.js";
import "./text.js";
import { handleOscCue } from "./osc.js";
import { handleMidiCue } from "./midi.js";
import { handleOscCtrlCue } from "./oscCtrl.js";
import { handleOscOutCue } from "./oscOut.js";
import { handleDisplayCue } from "./display.js";
import { handleUrlCue } from "./url.js";
import { handleDrawCue, stopDrawForElement } from "./draw.js";
import { handleUICue } from "./ui.js";
import { handleControlXYCue } from "./controlXY.js";
import { handleExtCue } from "./ext.js";
import { createNotation, createNotationSystem, pitchToMidi, midiToFreq, parsePitch, formatPitchName, isNotationEditing } from "../notation/notation.js";
import { sendOSC } from "../system/oscillaOSCClient.js";
import { isCondition, parseCondition, bindingSources } from "../parser/parserSignalRef.js";
import * as ParamBus from "../control/paramBus.js";

// =============================================================
// CONDITIONAL CUE EVALUATION (if: parameter)
// =============================================================

/**
 * Evaluate a condition object against current ParamBus values.
 * Used to gate cue triggering based on signal values.
 *
 * @param {Object} condition - Parsed condition object from parser
 * @param {string} condition.signal - Signal path (e.g., "mic.amp", "fader1.t")
 * @param {string|null} condition.op - Comparison operator (>, <, >=, <=, ==, !=) or null for truthy
 * @param {number|null} condition.threshold - Comparison value or null for truthy
 * @returns {boolean} True if condition is met
 */
export function evaluateCondition(condition) {
  if (!condition || !isCondition(condition)) {
    // Not a valid condition - treat as truthy (allow cue)
    return true;
  }

  const { signal, op, threshold } = condition;

  // Get current signal value from ParamBus
  const value = ParamBus.get(signal);

  // If signal doesn't exist yet, condition is not met
  if (value === undefined || value === null || !Number.isFinite(value)) {
    return false;
  }

  // If no operator, treat as truthy check (value > 0)
  if (op === null || op === undefined) {
    return value > 0;
  }

  // Apply comparison operator
  switch (op) {
    case '>':
      return value > threshold;
    case '<':
      return value < threshold;
    case '>=':
      return value >= threshold;
    case '<=':
      return value <= threshold;
    case '==':
      // Use small epsilon for floating point comparison
      return Math.abs(value - threshold) < 0.0001;
    case '!=':
      return Math.abs(value - threshold) >= 0.0001;
    default:
      // Unknown operator - allow cue
      return true;
  }
}

/**
 * Extract 'if' condition from AST (handles both args array and params object formats)
 * @param {Object} ast - Parsed cue AST
 * @returns {Object|null} Condition object or null
 */
function getIfCondition(ast) {
  if (!ast) return null;

  // Check params object (used by audio, audioPool, etc.)
  if (ast.params?.if) {
    return ast.params.if;
  }

  // Check direct property
  if (ast.if) {
    return ast.if;
  }

  // Check args array (used by rotate, scale, o2p, etc.)
  if (Array.isArray(ast.args)) {
    const ifArg = ast.args.find(a => a.type === 'if');
    if (ifArg) {
      return ifArg.value;
    }
  }

  return null;
}

// =============================================================
// PLAYBACK CLIENT FILTERING
// =============================================================

/**
 * Check if this client is in "hear all" mode.
 * A client is in hear-all mode if its name is NOT in the saved client
 * names list — unknown name = reviewer. Only clients whose name matches
 * a saved role get filtered playback.
 */
function isHearAllClient() {
  const name = window.localClientName || "";
  const savedNames = window.savedClientNames || [];
  return savedNames.length > 0 && !savedNames.includes(name);
}

/**
 * Check if this client should play a DSL cue based on playback targeting.
 * Used for SVG-authored cues (audio, audioPool, audioImpulse, synth).
 *
 * @param {object} ast - Parsed cue AST
 * @returns {boolean} - true if this client should play
 */
function shouldPlayOnThisClientDSL(ast) {
  // Targeting can live in either ast.params.playback (cueAudio etc.) or
  // among ast.args as { type: "playback", value: [...] } (cueSynth uses
  // the arg-list shape). Check both.
  let playbackClients = _audioCueParam(ast, "playback");
  const clientList = playbackClients == null ? null
    : (Array.isArray(playbackClients) ? playbackClients : [playbackClients]);

  // mute:true silences the cue everywhere, reviewers included.
  // playback:[__muted__] is the same thing as written by the old
  // session-layer audio objects.
  if (_isTruthyParam(_audioCueParam(ast, "mute")) || clientList?.includes("__muted__")) return false;

  // "Hear all" mode bypasses targeting
  if (isHearAllClient()) return true;

  // No targeting = all clients play
  if (!clientList || !clientList.length) return true;

  // Check if local client is in the list
  return clientList.includes(window.localClientName || "");
}

// =============================================================
//  Another member's sounds (a jam: oscillaLive.js runMirrored)
//
//  opts.peer: the line came from another member of the room. Its sounds
//  are noted by uid, so they can be stopped here when this machine stops
//  hearing the others; opts.quiet: it hears only its own (room(hear:mine)),
//  and they are passed by -- the line's visuals still run. A uid is the
//  room's: this machine's own line on it makes it this machine's again.
// =============================================================
const peerSounds = new Set();

function soundsHere(ast, opts) {
  const uid = _audioCueUid(ast);
  if (!opts?.peer) { if (uid) peerSounds.delete(uid); return true; }
  if (opts.quiet) return false;
  if (uid) peerSounds.add(uid);
  return true;
}

/** Stop the sounds other members' lines started here: how many uids. */
export function stopPeerSounds() {
  const uids = [...peerSounds];
  peerSounds.clear();
  if (uids.length) handleHushCue({ type: "cueHush", args: [{ type: "uid", value: uids }] });
  return uids.length;
}

// Flat params (audio family) or args-array (synth) AST styles
function _audioCueParam(ast, key) {
  return ast?.params?.[key] ?? ast?.[key] ?? ast?.args?.find?.(a => a?.type === key)?.value;
}

function _isTruthyParam(v) {
  return v === true || v === 1 || v === "1" || v === "true";
}

// Export for use in trigger.js
export { isHearAllClient };

////////////////////////////////////////////////////////////////////////////////////

export function parseCueParams(cueId) {
  // If caller passed an AST directly → just return it
  if (typeof cueId !== "string" && cueId?.type) {
    const ast = cueId;
    return { type: ast.type, params: ast.params || {}, ast };
  }

  // ----------------------------------------------------------
  // 1️⃣ Try Chevrotain AST Parsing (supports pause(4), nav(A), etc.)
  // ----------------------------------------------------------
  try {
    const ast = parseCueToAST(cueId.trim());

    // Ensure param object exists
    const cueParams = ast.params || {};

    return { type: ast.type, params: cueParams, ast };
  } catch (err) {
    // AST parse failed → fall through
  }

  // ----------------------------------------------------------
  // 2️⃣ No parser could handle this expression
  // ----------------------------------------------------------
  console.warn("[parseCueParams] No parser matched:", cueId);
  return null;
}

////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////
// ── View navigation is a ROOM event ──────────────────────────────────────
// A page opening — from the playhead, a button, the picker, a nav() or a
// page's after-action — and every way out of it — nav(scroll…), the picker,
// Escape, a double-tap, a countdown skip — happen on every client at once.
// The client where it happened tells the room (view_nav); the others run
// the same thing with isRemote=true, which is what keeps it from echoing.
//
// _viewNavQuiet > 0 while a view-nav runs, local or remote: the nested
// calls it makes (nav → page(...), nav → returnToScrollingScore) would each
// announce themselves. _viewNavRecent drops what this client has just done
// itself — every client runs a page pattern's after-action for itself when
// its own countdown ends, so the same nav arrives from each of the others.
window._viewNavQuiet = 0;
window._viewNavRecent = new Map();   // key → performance.now()
const VIEW_NAV_DEDUPE_MS = 3000;

export function viewNavKey(p) {
  if (p.kind === "page") return JSON.stringify(["page", p.pattern, p.onCompletion]);
  if (p.kind === "nav") return JSON.stringify(["nav", p.action, p.target, p.params?.repeats ?? null]);
  return p.kind;
}
export function viewNavSeenRecently(key) {
  const now = performance.now();
  for (const [k, t] of window._viewNavRecent) if (now - t > VIEW_NAV_DEDUPE_MS) window._viewNavRecent.delete(k);
  return window._viewNavRecent.has(key);
}
export function noteViewNav(key) { window._viewNavRecent.set(key, performance.now()); }

/** Run fn as a view-nav: nothing it triggers underneath announces itself. */
export function runViewNav(fn) {
  window._viewNavQuiet++;
  try { return fn(); } finally { window._viewNavQuiet--; }
}

export function broadcastViewNav(payload, cueElement = null) {
  if (window._viewNavQuiet > 0) return;
  if (window._independentPageView) return;   // a personal view from the URL, not the room's
  if (!(window.wsEnabled && window.socket?.readyState === WebSocket.OPEN)) return;
  if (payload.kind === "page" || payload.kind === "nav") noteViewNav(viewNavKey(payload));
  window.socket.send(JSON.stringify({
    type: "view_nav", ...payload,
    elementId: cueElement?.id || null,
    playheadX: transport.playheadX,
  }));
}

/**
 * trig:midi waits for a MIDI message, given as midi:note60 / cc64@1 / pc3:
 * the signal that message is ("midi.note60", midiIn.js), so it fires as any
 * signal trig: does. Any other trig comes back as written; a trig:midi with
 * no midi: comes back null, said.
 */
function midiTrigOf(ast, trigVal) {
  if (String(trigVal).toLowerCase() !== "midi") return trigVal;
  const spec = parseMidiSpec(ast.args?.find(a => a.type === "midi")?.value ?? ast.params?.midi);
  if (!spec) {
    console.warn("[trig:midi] needs midi:<note|cc|pc><number>[@channel] -- midi:note60, midi:cc64@1");
    return null;
  }
  wantMidiIn();
  return midiTrigSignal(spec);
}

/** The element is a registered playhead/auto cue: every client crosses it itself. */
export function isRegisteredCueElement(el) {
  return !!el && Array.isArray(window.cues) && window.cues.some(c => c.element === el);
}

// px either side of the playhead within which an element carried by a moving
// ancestor is measured live every frame (see checkCueTriggers)
const MOVER_LIVE_PX = 240;

// These five delay their own motion when tdelay is set, and deliberately do
// NOT delay arming or priming with it. The dispatcher must not delay them a
// second time on top.
const SELF_TIMED_CUES = new Set(["cueTrans", "cueRotate", "cueScale", "cueColor", "cueO2P"]);

let _clusterCount = 0;               // names a shape(n:) cluster written without a uid

/** Animation cues off:1 can stop, and the kind they register as. */
const OFF_KINDS = { cueRotate: "rotate", cueScale: "scale", cueTrans: "trans", cueColor: "color", cueO2P: "o2p" };

// Cues that act on an element, and so on the shape that leads their line.
const SHAPE_FOLLOWERS = /^(trans|rotate|scale|scaleXY|o2p|color|colour|fade|draw|pin)\s*\(/;

/**
 * shape(...) rotate(...) o2p(...): the shape first, then each cue after it
 * acting on that shape. The first one without a uid takes the shape's, so
 * its signals read as the shape's (sh1.angle); later ones get sh1_2, sh1_3
 * -- the animation registry holds one entry per uid. Other cues on the line
 * (a synth, say) run as they would on their own.
 */
function runShapeLine(exprs, isRemote, force, cueElement, opts) {
  const el = handleCueTrigger(exprs[0], isRemote, force, cueElement, opts);
  if (!el) return null;
  let n = 0;
  for (let expr of exprs.slice(1)) {
    // self. is this shape: follow(self.y, ...), follow(self.tvel, ...) -- in
    // a cluster, each member's own (me., its first name, as well)
    expr = expr.replace(/\b(?:self|me)\./g, `${el.id}.`);
    if (!SHAPE_FOLLOWERS.test(expr)) {
      // a sound on a shape's line is the shape's: named after it, so each
      // member of a cluster has its own voice, and removing the shape
      // silences it
      const own = /^(synth|audio|audioPool|audioImpulse)\s*\(/.test(expr) && !/\buid\s*:/.test(expr)
        ? expr.replace("(", `(uid:${el.id}${/^\w+\s*\(\s*\)/.test(expr) ? "" : ", "}`) : expr;
      handleCueTrigger(own, isRemote, force, cueElement, opts);
      continue;
    }
    n++;
    const named = /\buid\s*:/.test(expr) ? expr
      : expr.replace("(", `(uid:${n === 1 ? el.id : `${el.id}_${n}`}${/^\w+\s*\(\s*\)/.test(expr) ? "" : ", "}`);
    handleCueTrigger(named, isRemote, force, el, opts);
  }
  return el;
}

/**
 * The random numbers a console line rolls again on its own (each hit of a
 * signal, each member of a cluster, each voice): from the run's seed
 * (opts.seed) when it has one, so the projector window rolls the laptop's;
 * a score's cue, with none, rolls its own.
 */
function rollsFor(opts, line, ...keys) {
  return opts?.seed != null ? seededRandom(opts.seed, line, ...keys) : Math.random;
}

/**
 * A number of a cue read flat (speed): rand() / choose() rolled each run.
 * What is still no number (a pattern, which has no step here) is dropped
 * with a warning.
 */
function rollNumber(v, what) {
  if (v == null || typeof v !== "object") return v;
  const r = evalMaybeRandom(v);
  if (r != null && typeof r !== "object") return r;
  console.warn(`[${what}] takes a number, rand() or choose(), not ${JSON.stringify(v).slice(0, 40)}`);
  return null;
}

/**
 * opts.live: the live console sent it. A cue that is already running then
 * carries on with the new parameters where that means something (o2p goes
 * on from where its object is) instead of starting over.
 */
export function handleCueTrigger(cueExprOrAst, isRemote = false, force = false, cueElement = null, opts = {}) {
  // a console line: the signals it binds (follow(...)) are its own, so the
  // console can mark the line active and Ctrl+Enter can stop them. (Each
  // cue a line runs is claimed when its own handler is done: a shape's
  // scale(...) is loaded and run after the shape.)
  if (opts.live && opts.source) {
    return bindingsFrom(opts.source, () => dispatchCue(cueExprOrAst, isRemote, force, cueElement, opts));
  }
  return dispatchCue(cueExprOrAst, isRemote, force, cueElement, opts);
}

// cues that resolve relative values (rate:*0.9) against what they have now
const REL_AWARE = new Set(["cueSay", "cueSynth", "cueAudio", "cueLevel", "cueFx", "cueViz"]);
const relHinted = new Set();

// a line's every: count (its text -> makeEvery), run by run
const everyCounts = new Map();

function dispatchCue(cueExprOrAst, isRemote = false, force = false, cueElement = null, opts = {}) {
  // ---------------------------
  // 🔍 STEP 1: Normalise AST
  // ---------------------------
  let ast = null;
  let cueId = null;

  const isAstObject = typeof cueExprOrAst === "object" && cueExprOrAst.type;

  if (isAstObject) {
    ast = cueExprOrAst;
    cueId = ast.raw || ast.id || null;
  } else {
    cueId = String(cueExprOrAst).trim();
  }

  // ---------------------------
  //  STEP 1b: COMPOSITE DSL SPLIT
  //  e.g. "audio(src:x) rotate(dur:2)" → trigger each part separately
  // ---------------------------
  // shape(n:12, ...) ...: a cluster, one line per member (cueUtils.js). The
  // live console expands it itself, to roll each member before it is sent.
  if (!isAstObject && cueId && /^\s*shape\s*\(/.test(cueId)) {
    const members = expandCluster(cueId, `cluster${++_clusterCount}`);
    if (members) {
      for (const m of members) handleCueTrigger(m, isRemote, force, cueElement, opts);
      return null;
    }
  }

  if (!isAstObject && cueId) {
    let exprs = splitCueId(cueId);
    // A station's other cues are its stay: the route runs them on arrival
    // (route.js). Triggered whole (next:<station id>), only station() runs.
    const station = exprs.find(e => /^station\s*\(/.test(e));
    if (station) exprs = [station];
    // A line led by shape() -- or use(), a drawing from blocks/ -- makes
    // it, then hands it to the rest.
    if (exprs.length > 1 && /^(shape|use)\s*\(/.test(exprs[0])) {
      return runShapeLine(exprs, isRemote, force, cueElement, opts);
    }
    if (exprs.length > 1) {
      for (const expr of exprs) {
        handleCueTrigger(expr, isRemote, force, cueElement, opts);
      }
      return;
    }
    if (station) cueId = station;
  }

  // A score built from next: hand-overs has no position to read, so this is
  // where the readout learns which named step is running (sceneReadout.js).
  // Both call styles land here — cueNext hands over a DSL string, the playhead
  // an AST — and a step is named by its uid: or by the element it sits on.
  noteScene(
    isAstObject
      ? (ast.uid ?? ast.params?.uid ?? ast.args?.find(a => a.type === "uid")?.value ?? null)
      : (cueId?.match(/\buid\s*:\s*([A-Za-z_][\w-]*)/)?.[1] ?? null),
    cueElement?.getAttribute?.("id") ?? null
  );

  // ---------------------------
  //  STEP 2: DEDUPE RULES
  // ---------------------------
  // Buttons pass force=true → retrigger always
  // Page cues → retrigger always
  // Scroll cues → dedupe normally
  // ---------------------------

  // Page / nav cues must ALWAYS retrigger
  const isPageCue =
    cueId?.startsWith("page(") ||
    cueId?.startsWith("page:") ||
    cueId?.includes("nav(") ||
    cueId?.includes("nav:");

  if (!force && !isAstObject && !isPageCue) {
    // Initialise registry
    if (!window.triggeredCues) window.triggeredCues = new Set();

    // If already triggered, skip
    if (window.triggeredCues.has(cueId)) {
      return;
    }

    // Track only non-button, non-page cues
    window.triggeredCues.add(cueId);
  }

  // ---------------------------
  // 🔍 STEP 3: Parse cue into AST if needed
  // ---------------------------
  // use(...) reaching here was run, not loaded: a score's use() placeholders
  // are filled before any cue runs (preProcessReuse.js), so this is the
  // live console's -- a block put on the stage (useLive.js).
  if (!ast && /^use\s*\(/.test(cueId)) {
    const use = parseUseDirective(cueId);
    return use ? handleUseLive(use) : null;
  }
  if (!ast) {
    try {
      ast = parseCueToAST(cueId, { quiet: true });
    } catch (err) {
      console.error("[CUE] Could not parse cue expression:", cueId, err.parseErrors ?? err);
      return;
    }
  }

  if (!ast) {
    console.error("[CueDSL] No AST resolved — cannot proceed.");
    return;
  }

  // chance:1/6 -- the cue happens with that chance, each time it is run: a
  // shape, a text, a hit of a signal. A sound with steps of its own draws
  // for each step (synth.js); a line that starts watching a signal always
  // starts -- its hits are drawn for, each one.
  // every:4 -- only each 4th time it is run (every:peuclid(3, 8) the times
  // that pattern gives a 1), counted per line; a line that starts watching
  // starts its count again. chance: then draws only for the times let through.
  const chance = readChance(ast);
  const every = readEvery(ast);
  if ((chance != null || every != null) && !stepsItself(ast)) {
    const trig = ast.trig ?? ast.params?.trig ?? ast.args?.find(a => a?.type === "trig")?.value;
    const watches = trig != null && !opts._fromTrig;
    const line = String(opts.source ?? cueId ?? "");
    if (every != null && watches) everyCounts.delete(line);
    if (every != null && !watches) {
      if (!everyCounts.has(line)) everyCounts.set(line, makeEvery(every));
      if (everyCounts.get(line)?.on() === false) return null;
    }
    if (chance != null && !watches && !(Math.random() < chance)) return null;
  }

  // ---------------------------
  // STEP 3.3: synth(uid:x, ..., trig:wait) -- learnt, not played: solo(x)
  // and bang(x) start it (they leave the trig: off, or say _start)
  // ---------------------------
  if (opts.live && !opts._start && !opts._fromTrig && ast.type === "cueSynth") {
    const trigVal = ast.args?.find(a => a.type === "trig")?.value ?? ast.params?.trig;
    const uid = ast.args?.find(a => a.type === "uid")?.value ?? ast.params?.uid;
    if (uid && WAITS.test(String(trigVal ?? ""))) {
      if (!(opts.peer && opts.quiet)) learnSynthLine(uid, opts.source ?? cueId);   // not learnt: another's bang() finds nothing
      return;
    }
  }

  // ---------------------------
  // STEP 3.4: a console line with a signal trig: -- run on each hit
  // (trig:midi, midi:note60 too: the note's signal, midiTrigOf)
  // ---------------------------
  // viz(s1, view:choose(polar, xy, phase), trig:s1.hit): the line is not run
  // now but each time the signal rises, rolled afresh (choose(), rand()) --
  // as a score's trig:"adc.onset>0.5" is. Run again, it replaces its watcher
  // (one per cue and name); trig:off stops it, and so does hush().
  if (opts.live && !opts._fromTrig && typeof cueExprOrAst === "string") {
    const trigVal = midiTrigOf(ast, ast.args?.find(a => a.type === "trig")?.value ?? ast.params?.trig ?? ast.trig);
    if (trigVal === null) return;
    const off = /^(off|0|none)$/i.test(String(trigVal ?? ""));
    if (isSignalTrig(trigVal) || off) {
      const who = /\buid\s*:\s*([\w-]+)/.exec(cueId)?.[1] ?? /\(\s*\[?\s*([\w-]+)/.exec(cueId)?.[1] ?? "";
      const key = `live:${ast.type}:${cueElement?.id ?? ""}:${who}`;
      if (off) { dropSignalTrigger(key); return; }
      // the line as written (the console rolled choose() / rand() before
      // sending it: each hit rolls them again)
      const line = opts.source ?? cueId;
      // its patterns step a hit, from the top each time the line is set watching (patternSteps.js)
      const step = patternStepper();
      registerSignalTrigger(String(trigVal), (value, hit) => {
        // a console run's seed (opts.seed): this hit's numbers are the same in
        // the laptop's window and the projector's
        const rolled = parseCueToAST(settleRandom(line, rollsFor(opts, line, hit)));
        if (!rolled) return;
        const once = step({ ...rolled, args: rolled.args?.filter(a => a.type !== "trig") });
        // a text's cycle / shuffle steps one item a hit (text.js)
        if (once.type === "cueText") once.args.push({ type: "_fromTrig", value: true });
        // a synth struck by a hit sounds once a hit -- a drum would else go on
        // beating every step -- unless its line gives its own rhythm (dur:)
        if (once.type === "cueSynth" && !once.args.some(a => ["dur", "stopAfter", "once"].includes(a.type))) {
          once.args.push({ type: "once", value: 1 });
        }
        handleCueTrigger(once, isRemote, true, cueElement, { ...opts, _fromTrig: true, _hit: hit });
      }, { key, source: line, hits: opts.hits?.[line] ?? 0 });
      return;
    }
  }

  // ---------------------------
  // STEP 3.45: a cluster given to an animation that works in place
  // (stars > rotate(rand(0, 360), dur:0.5, trig:walker.hit)): each member,
  // about its own middle, its rand() rolled for it -- the group turned as
  // one swung the members out of their places (a grid's cells). A line on a
  // signal comes here on each hit (STEP 3.4: one watcher, the cluster's).
  // ---------------------------
  if (opts.live && !opts._member && ast && CLUSTER_IN_PLACE.has(ast.type) && cueElement?.classList?.contains("oscilla-shape-cluster")) {
    const line = opts.source ?? (typeof cueExprOrAst === "string" ? cueExprOrAst : null);
    const random = line ? rollsFor(opts, line, opts._hit ?? "run", "members") : null;
    for (const member of cueElement.querySelectorAll(".oscilla-shape")) {
      let one = ast;
      if (line) {
        const rolled = parseCueToAST(settleRandom(line, random));
        if (rolled) one = { ...rolled, args: rolled.args?.filter(a => a.type !== "trig") };
      }
      handleCueTrigger(one, isRemote, true, member, { ...opts, _fromTrig: true, _member: true });
    }
    return;
  }

  // ---------------------------
  // 🔀 STEP 3.5: CONDITIONAL CHECK (if: parameter)
  // ---------------------------
  // If cue has an 'if' condition, evaluate it against ParamBus values.
  // Only trigger the cue if condition is met.
  const ifCondition = getIfCondition(ast);
  if (ifCondition && isCondition(ifCondition)) {
    if (!evaluateCondition(ifCondition)) {
      // Condition not met - skip this cue
      // Remove from triggeredCues so it can fire later when condition is met
      if (cueId && window.triggeredCues) {
        window.triggeredCues.delete(cueId);
      }
      return;
    }
  }

  // ---------------------------
  // ⏱ STEP 3.6: tdelay — wait, then act
  // ---------------------------
  // trans, rotate, scale, color and o2p read tdelay themselves, and mean
  // something more specific by it: the motion waits while the arming and the
  // priming happen at once, so an armed element is ready to click before it
  // moves. Leave those alone.
  //
  // Every other cue has no tdelay of its own, and the editor offers it on
  // most of them. Deferring the dispatch gives all of them the plain reading
  // of the word.
  //
  // The cue is already in triggeredCues by now (STEP 2), so waiting cannot
  // make it fire twice.
  if (!SELF_TIMED_CUES.has(ast.type) && !ast._tdelayed) {
    const td = secs(readTdelay(ast));     // beats (beats.js)
    if (td > 0) {
      const later = { ...ast, _tdelayed: true };
      // tracked by the sequence: a stop drops it, a pause holds it when due
      sequenceLater(() => {
        // Gone from the score while it waited (a page closed, a project
        // switched): nothing left to act on.
        if (cueElement && !cueElement.isConnected) return;
        handleCueTrigger(later, isRemote, force, cueElement, opts);
      }, td * 1000);
      return;
    }
  }

  // ---------------------------
  // ⏹ STEP 3.7: off:1 -- stop this kind of animation on the element
  // (organ > rotate(off:1), from the live console), leaving it where it is
  // ---------------------------
  const offKind = OFF_KINDS[ast.type];
  if (offKind && ast.args?.some(a => a.type === "off" && Number(a.value) !== 0)) {
    if (offKind === "o2p") releaseO2P(cueElement);
    stopAnimationsOn(cueElement, offKind);
    return null;
  }

  // ---------------------------
  // 👥 STEP 3.8: a group's name -- say(agents, ...) runs for each member
  // (groups.js); a list of names with groups in it, opened
  // ---------------------------
  const each = expandGroups(ast);
  if (each) return Promise.all(each.map(a => dispatchCue(a, isRemote, true, cueElement, opts)));

  // text(..., style:agent): the preset's settings and look (textStyles.js)
  const styled = expandStyle(ast);
  if (styled) ast = styled;

  // a relative value (rate:*0.9) on a cue that does not know what it is
  // now: from the value's own default, with a word once (relative.js)
  if (!REL_AWARE.has(ast.type) && hasRel(ast.args)) {
    if (!relHinted.has(ast.type)) {
      relHinted.add(ast.type);
      console.warn(`[cue] ${ast.type.replace(/^cue/, "").toLowerCase()}(): *k / +=k count from 1 / 0 here -- they follow what a value is now on say, synth, audio, level, fx and viz`);
    }
    ast = { ...ast, args: ast.args.map(a => (isRel(a.value) ? { ...a, value: applyRel(null, a.value) } : a)) };
  }

  // ---------------------------
  // 🚀 STEP 4: DISPATCH
  // ---------------------------
  switch (ast.type) {

    case "cueUi":
      return handleUICue(ast, cueElement);

    case "cueExt":
      return handleExtCue(ast, cueElement);

    // draw(): start leaving ink wherever something else takes this element
    case "cueDraw":
      return handleDrawCue(ast, cueElement);

    // Animation handlers
    case "cueTrans": {
      handleTransCue(ast, cueElement, { fromCueTrigger: true });
      break;
    }

    case "cueScale": {
      handleScaleCue(ast, cueElement, { fromCueTrigger: true });
      return;
    }

    case "cueRotate": {
      handleRotateCue(cueElement, ast.args, { fromCueTrigger: true });
      return;
    }

    case "cueO2P": {
      handleO2PCue(cueElement, ast.args, { fromCueTrigger: true, live: !!opts.live });
      return;
    }

    case "cueColor": {
      handleColorCue(cueElement, ast.args, { fromCueTrigger: true });
      return;
    }

    case "cueOsc": {
      handleOscCue(ast, cueElement, { fromCueTrigger: true });
      return; // osc has no children
    }

    case "cueMidi": {
      handleMidiCue(ast, cueElement, { fromCueTrigger: true });
      return;
    }

    case "cueControlXY": {
      handleControlXYCue(cueElement, ast.args || [], { fromCueTrigger: true });
      return;
    }

    case "cueFade": return handleFadeCueFromAST(ast, cueElement);
    case "cueAdc": return handleAdcCue(ast, cueElement);
    case "cueRandom": return handleRandomCue(ast, cueElement);
    case "cueStopwatch": return handleStopwatchCue(ast, cueElement);
    case "cueVideo": return handleVideoCueFromAST(ast, cueElement);

    case "cueImage":
      import("./image.js")
        .then(mod => mod.handleImageCue(ast, cueElement))
        .catch(err => console.error("[CueDSL] Failed to load image.js module:", err));
      return;

    case "cueText": {
      // text(uid:x, stop:1): that text, gone -- a looping one has no end
      const stop = ast.args?.find(a => a.type === "stop")?.value;
      const uid = ast.args?.find(a => a.type === "uid")?.value;
      import("./text.js")
        .then(mod => stop != null && uid != null && Number(stop) !== 0
          ? mod.stopCueText(String(uid))
          : mod.handleCueTextFromAST(ast, cueElement))
        .catch(err => console.error("[CueDSL] Failed to load text.js module:", err));
      return;
    }

    case "cueMetronome":
      return handleMetronomeCue(ast, cueElement);

    // Speed cues support ramps
    case "cueSpeed": {
      const start = transport.speedMultiplier ?? 1.0;
      const value = rollNumber(ast.value, "speed");
      const end = value ?? start;
      const dur = secs(rollNumber(ast.dur ?? null, "speed dur:"));     // beats (beats.js); "edge" as it is
      // a ramp runs on actual speeds (speed.js), so its target is the cue
      // value times the base, as handleSpeedCue makes it for an instant set
      const rampEnd = value != null ? Number(value) * (window.baseSpeedMultiplier || 1) : start;

      if (dur === "edge" && cueElement) {
        // dur:edge — ramp across the element's own drawn width (see speed.js)
        return handleSpeedRampOverElement(start, rampEnd, cueElement, ast.ease);
      }
      if (dur && dur > 0) {
        return handleSpeedRamp(start, rampEnd, dur, ast.ease);
      } else {
        return handleSpeedCue(`speed(${end})`, end);
      }
    }

    case "cueHush":
      // every sound stopped: a scene's lines still waiting behind a wait() are dropped too (liveScenes.js)
      if (!ast.args?.length && ast.uid == null && ast.params?.uid == null) window.dispatchEvent(new Event("oscilla:hush-all"));
      return handleHushCue(ast);
    // wait(16) means something only in a block run in turn (liveScenes.js runInTurn); alone, nothing
    case "cueWait": return null;
    case "cueStill": return import("./still.js").then(m => m.handleStillCue(ast));
    case "cueCapture": return import("../system/capture.js").then(m => m.handleCaptureCue(ast));
    case "cueResources": return import("../system/resources.js").then(m => m.handleResourcesCue(ast));
    case "cueScreen": return import("../system/screenFx.js").then(m => m.handleScreenCue(ast));
    case "cueGrid": return handleGridCue(ast);
    case "cueCam": return import("./cam.js").then(m => m.handleCamCue(ast, cueElement));
    case "cueScene":
    case "cueScenes": return import("../system/sceneHook.js").then(m => m.handleSceneCue(ast));
    case "cueConsole": return import("../system/consoleHook.js").then(m => m.handleConsoleCue(ast));
    case "cueScatter": return import("./scatter.js").then(m => m.handleScatterCue(ast, cueElement));
    case "cueStrobe": return import("./strobe.js").then(m => m.handleStrobeCue(ast, cueElement));
    case "cueVu": return import("./vu.js").then(m => m.handleVuCue(ast));
    // at once, not after a load: a level() fading in, run before its sound,
    // must be waiting when the sound starts (a moment later it is heard)
    case "cueLevel": return handleLevelCue(ast);
    case "cueXfade": return handleXfadeCue(ast);
    case "cueHold": return import("./hold.js").then(m => m.handleHoldCue(ast));
    case "cueBang": return import("./bang.js").then(m => m.handleBangCue(ast));
    case "cueGate": return import("./gate.js").then(m => m.handleGateCue(ast));
    case "cueSeq": return import("./seq.js").then(m => m.handleSeqCue(ast));
    case "cueSay":
      if (!soundsHere(ast, opts)) return;
      return import("./say.js").then(m => m.handleSayCue(ast));
    case "cueFx": return import("./audio/fxBus.js").then(m => m.handleFxCue(ast));
    case "cueGroup": return handleGroupCue(ast);
    case "cueSolo": return import("./audio/solo.js").then(m => m.handleSoloCue(ast));
    case "cueSpeakers": return import("./audio/speakers.js").then(m => m.handleSpeakersCue(ast));
    case "cueStyle": return handleStyleCue(ast);
    case "cueTempo": return handleTempoCue(ast);
    case "cueShape": return handleShapeCue(ast, cueElement);
    case "cueViz": return handleVizCue(ast);
    case "cuePalette": return handlePaletteCue(ast);

    // AUDIO
    case "cueAudio":
      if (!shouldPlayOnThisClientDSL(ast) || !soundsHere(ast, opts)) return;
      return handleAudioCue(ast, cueElement);

    // AUDIO POOL — single-event randomised selection
    case "cueAudioPool": {
      if (!shouldPlayOnThisClientDSL(ast) || !soundsHere(ast, opts)) return;
      return handleAudioPoolCue(ast, cueElement, { fromCueTrigger: true });
    }

    // AUDIO IMPULSE — long-running process
    case "cueAudioImpulse": {
      if (!shouldPlayOnThisClientDSL(ast) || !soundsHere(ast, opts)) return;
      return handleAudioImpulseCue(ast, cueElement, { fromCueTrigger: true });
    }

    case "cueSynth": {
      if (!shouldPlayOnThisClientDSL(ast) || !soundsHere(ast, opts)) return;
      // run by hand: a sound solo() started is yours again (its end will not stop it)
      if (!ast._bySolo) soloOwned.delete(String(ast.args?.find(a => a.type === "uid")?.value ?? ""));
      // Ctrl+Alt+Enter: each voice its own roll of the line's rand()s, as a
      // Ctrl+Enter would give it (dur:rand(6, 20) a number, not a formula)
      const voices = opts?.allVoices && cueId ? rollsFor(opts, cueId, "voices") : null;
      const reroll = voices ? () => parseCueToAST(settleRandom(cueId, voices)) : null;
      // source: the line as written, rand()s and all (bang(s) rolls it again)
      return handleSynthCue(ast, cueElement, { fromCueTrigger: true, live: !!opts?.live, allVoices: !!opts?.allVoices, reroll, source: opts?.source ?? cueId });
    }

    // Standalone pin() — normally registered at scan/registration time and
    // never dispatched, but a stray trigger (click, manual) lands here.
    // registerPin is idempotent, so just ensure the pin exists.
    case "cuePin":
      if (cueElement) {
        if (ast.release) registerPinRelease(cueElement, ast.release);
        else registerPin(cueElement, ast.dur, ast);
      }
      return;

    default: {
      // the score's own cues, page() nav() pause() zoom() ... (scoreCues.js)
      const handler = appCue(ast.type);
      if (handler) return handler(ast, cueElement, { isRemote });
      console.warn(`[CueDSL] ⚠ Unsupported cue type: ${ast.type}`);
      return;
    }
  }
}

window.handleCueTrigger = handleCueTrigger;


// =============================================================
//  Audio cue click gestures — aligned with the session-layer
//  audio objects (interactionSurface.js):
//    single click : mono audio → pause/resume toggle (trigger if silent)
//                   pool/poly  → trigger a new voice immediately
//                   impulse/synth → trigger (after double-click window)
//    double click : stop all voices of this cue
//    click + hold : (500 ms, while playing) stop all voices
//    shift+click  : pool/poly → stop the oldest voice
//  Video keeps the plain immediate-fire click.
//
//  The AST is read from element._audioCueAst at event time (not
//  captured in the closure) so DSL-editor re-saves take effect
//  without rebinding.
// =============================================================
const _AUDIO_GESTURE_LONG_PRESS_MS = 500;
const _AUDIO_GESTURE_DOUBLE_CLICK_MS = 300;

function _audioCueUid(ast) {
  // Flat (audio/pool/impulse) and args-array (synth) AST styles
  const fromArgs = (key) => ast?.args?.find?.(a => a.type === key)?.value;
  const uid = ast?.uid ?? ast?.params?.uid ?? fromArgs("uid")
           ?? ast?.src ?? ast?.params?.src ?? ast?.params?.path ?? fromArgs("src");
  return typeof uid === "string" ? uid.trim() : null;
}

/** A synth cue's voice uid, made as synth.js makes it: uid:, or one from the element's id. */
function _cueSynthUid(ast, el) {
  const uid = String(_audioCueParam(ast, "uid") ?? "").trim();
  if (uid) return uid;
  if (el?.id) return `synth-${el.id.substring(0, 50).replace(/[^a-zA-Z0-9]/g, '_')}`;
  return el?._autoSynthUid ?? null;
}

function _audioCueVoices(uid) {
  const out = [];
  const reg = window.activeAudioCues;
  if (reg) {
    for (const [k, v] of reg.entries()) {
      if (k === uid || k.startsWith(`${uid}__`)) out.push([k, v]);
    }
  }
  return out;
}

function _audioCueIsPlaying(ast) {
  const uid = _audioCueUid(ast);
  if (!uid) return false;
  if (_audioCueVoices(uid).length) return true;
  if (window.audioImpulses?.has(uid)) return true;
  const synthReg = window.activeSynthVoices;
  if (synthReg) {
    for (const k of synthReg.keys()) {
      if (k === uid || k.startsWith(`${uid}__`)) return true;
    }
  }
  return false;
}

function _audioCueIsPlayingAnywhere(ast) {
  return _audioCueIsPlaying(ast) || (_isNetworkedAudioCue(ast) && _audioCuePlayingElsewhere(_audioCueUid(ast)));
}

function _audioCueStopAll(ast, element) {
  const uid = _audioCueUid(ast);
  if (!uid) return;
  const fadeOut = secs(ast.fadeOut ?? ast.params?.fadeOut ?? 0.3);     // beats (beats.js)
  // Cancel pending spread-poly setTimeout voices (same as region-exit logic)
  if (element) element._spreadGen = (element._spreadGen || 0) + 1;
  for (const [, voice] of _audioCueVoices(uid)) {
    // Don't delete from the registry — the voice's own cleanup handles it
    // after the fadeout, which also resets the waveform cursor.
    try { voice?.stop?.(fadeOut); } catch (err) { console.warn("[CueDSL] voice stop failed:", err); }
  }
  if (window.audioImpulses?.has(uid)) stopAudioImpulse(uid, fadeOut);
  const synthReg = window.activeSynthVoices;
  if (synthReg) {
    for (const k of [...synthReg.keys()]) {
      if (k === uid || k.startsWith(`${uid}__`)) stopSynth(k, fadeOut);
    }
  }
}

function _audioCueStopOldestVoice(ast) {
  const uid = _audioCueUid(ast);
  if (!uid) return;
  const fadeOut = secs(ast.fadeOut ?? ast.params?.fadeOut ?? 0.3);     // beats (beats.js)
  const voices = _audioCueVoices(uid); // Map preserves insertion order
  if (voices.length) { try { voices[0][1]?.stop?.(fadeOut); } catch (err) { console.warn("[CueDSL] voice stop failed:", err); } }
}

// =============================================================
//  Clicks across the network
//
//  A click on a sound plays where the click is, unless the sound is
//  networked — scope:shared, or a playback:[…] list naming who plays it.
//  Then the click goes to every client (or to the ones picked in the
//  client list) and each plays it if playback: lets it. The clicker plays
//  too when it qualifies; the conductor that is not in playback: only
//  sends. A pool's file / amp / pan / speed draw travels with the click so
//  every client plays the same thing.
//
//  Playhead triggers stay local: every client crosses the cue itself.
// =============================================================

const _AUDIO_GESTURE_TYPES = ["cueAudio", "cueAudioPool", "cueAudioImpulse", "cueSynth"];

// uid → Set of client names playing it elsewhere (from their own reports)
const _remotePlaying = new Map();
// uid → { element, seen, t0 } for the sounds this client is playing because
// of a networked click — so it can say when they end
const _netPlayingHere = new Map();

function _isNetworkedAudioCue(ast) {
  if (!_AUDIO_GESTURE_TYPES.includes(ast?.type)) return false;
  if (String(_audioCueParam(ast, "scope") ?? "").toLowerCase() === "shared") return true;
  const pb = _audioCueParam(ast, "playback");
  return Array.isArray(pb) ? pb.length > 0 : !!pb;
}

function _audioNetSend(type, payload) {
  const sock = window.socket;
  if (!sock || sock.readyState !== WebSocket.OPEN) return;
  sock.send(JSON.stringify({ type, project: window.currentProject || "", ...payload }));
}

function _audioCuePlayingElsewhere(uid) {
  return !!uid && (_remotePlaying.get(uid)?.size ?? 0) > 0;
}

function _setRemotePlaying(uid, client, on, element) {
  if (!uid) return;
  let set = _remotePlaying.get(uid);
  if (on) {
    if (!set) _remotePlaying.set(uid, set = new Set());
    set.add(client || "?");
  } else if (set) {
    if (client) set.delete(client); else set.clear();
    if (!set.size) _remotePlaying.delete(uid);
  }
  // The extent line animates while anyone plays it
  const rec = element ? _dslExtentLines.get(element) : null;
  if (rec) {
    const key = `${uid}__remote`;
    if (_audioCuePlayingElsewhere(uid)) { rec.active.add(key); rec.startAnim(); }
    else { rec.active.delete(key); if (!rec.active.size) rec.stopAnim(); }
  }
}

function _audioCueTargetsMe(targets) {
  return !targets?.length || targets.includes(window.localClientName || "");
}

let _netPlayingTimer = null;

function _noteNetPlaying(uid, element) {
  if (!uid) return;
  _netPlayingHere.set(uid, { element, seen: false, t0: Date.now() });
  _audioNetSend("dsl_audio_state", { uid, elId: element?.getAttribute?.("id"), playing: true, client: window.localClientName || "" });
  // When a networked sound playing here runs out, tell the others. Polled:
  // file voices announce their end, synths and impulses do not.
  if (!_netPlayingTimer) _netPlayingTimer = setInterval(_checkNetPlayingEnded, 400);
}

function _checkNetPlayingEnded() {
  for (const [uid, rec] of _netPlayingHere) {
    const { element } = rec;
    const ast = element?._audioCueAst;
    if (ast && element.isConnected && _audioCueIsPlaying(ast)) { rec.seen = true; continue; }
    // A file still loading has no voice yet: not ended until it has played,
    // or never started at all
    if (!rec.seen && element?.isConnected && Date.now() - rec.t0 < 15000) continue;
    _netPlayingHere.delete(uid);
    _audioNetSend("dsl_audio_state", { uid, elId: element?.getAttribute?.("id"), playing: false, client: window.localClientName || "" });
  }
  if (!_netPlayingHere.size) { clearInterval(_netPlayingTimer); _netPlayingTimer = null; }
}

function _playAudioCueHere(ast, element, synced = null) {
  element._clickTriggered = true;
  let p;
  if (ast.type === "cueAudioPool") {
    p = handleAudioPoolCue(ast, element, { fromCueTrigger: true, ...(synced || {}) });
  } else {
    handleCueTrigger(ast, false, true, element);
  }
  element._clickTriggered = false;
  return p;
}

async function _fireNetworkedAudioCue(ast, element) {
  const uid = _audioCueUid(ast);
  const targets = getSelectedClients();
  const msg = { uid, elId: element.getAttribute("id") };
  if (targets.length) msg.targetClients = targets;

  if (shouldPlayOnThisClientDSL(ast) && _audioCueTargetsMe(targets)) {
    const result = await _playAudioCueHere(ast, element);
    if (result?.selectedFile) {
      msg.sync = { syncedFile: result.selectedFile, syncedAmp: result.amp, syncedPan: result.pan, syncedSpeed: result.speed };
    }
    _noteNetPlaying(uid, element);
  }
  _audioNetSend("dsl_audio_trigger", msg);
}

function _stopNetworkedAudioCue(ast, element) {
  const uid = _audioCueUid(ast);
  _audioCueStopAll(ast, element);
  _netPlayingHere.delete(uid);
  _setRemotePlaying(uid, null, false, element);
  const targets = getSelectedClients();
  _audioNetSend("dsl_audio_stop", { uid, elId: element.getAttribute("id"), ...(targets.length ? { targetClients: targets } : {}) });
}

// Stop from any gesture: everywhere when the sound is networked
function _audioCueStopFromGesture(ast, element) {
  if (_isNetworkedAudioCue(ast)) _stopNetworkedAudioCue(ast, element);
  else _audioCueStopAll(ast, element);
}

function _findAudioCueElement(elId, uid) {
  const byId = elId ? document.getElementById(elId) : null;
  if (byId?._audioCueAst) return byId;
  if (!uid) return null;
  for (const el of document.querySelectorAll("[data-cue-clickable]")) {
    if (el._audioCueAst && _audioCueUid(el._audioCueAst) === uid) return el;
  }
  return null;
}

function _isForThisProject(data) {
  return !data.project || !window.currentProject || data.project === window.currentProject;
}

export function handleRemoteAudioCueTrigger(data) {
  if (!_isForThisProject(data)) return;
  const element = _findAudioCueElement(data.elId, data.uid);
  const ast = element?._audioCueAst;
  if (!ast) return;
  const uid = _audioCueUid(ast);
  if (!shouldPlayOnThisClientDSL(ast)) return;
  // A running impulse or synth is not restarted by someone else's click
  if ((ast.type === "cueAudioImpulse" || ast.type === "cueSynth") && _audioCueIsPlaying(ast)) return;
  const ctx = window.sharedAudioCtx;
  if (ctx?.state === "suspended") ctx.resume().catch(() => { /* resume refused without a user gesture */ });
  _playAudioCueHere(ast, element, data.sync);
  _noteNetPlaying(uid, element);
}

export function handleRemoteAudioCueStop(data) {
  if (!_isForThisProject(data)) return;
  const element = _findAudioCueElement(data.elId, data.uid);
  const ast = element?._audioCueAst;
  if (!ast) return;
  const uid = _audioCueUid(ast);
  _audioCueStopAll(ast, element);
  _netPlayingHere.delete(uid);
  _setRemotePlaying(uid, null, false, element);
}

export function handleRemoteAudioCueState(data) {
  if (!_isForThisProject(data)) return;
  const element = _findAudioCueElement(data.elId, data.uid);
  _setRemotePlaying(data.uid, data.client, !!data.playing, element);
}

// =============================================================
//  DSL extent:N — region width in SVG world units measured from the cue
//  element's left edge, stored as element._dslExtentW; checkCueTriggers
//  measures the region from it live each frame.
//
//  Also renders a persistent extent line in the score (a dashed line from
//  the element's right edge
//  to the extent end, vertical end bar, dashes animate while voices play)
//  so musicians can read the active region as part of the score.
// =============================================================

const _dslExtentLines = new Map(); // element → record

function _extentDashPattern(astType) {
  switch (astType) {
    case "cueAudio":     return "2 6"; // dots
    case "cueAudioPool": return "4 4";
    case "cueSynth":     return "3 3";
    case "cueAudioImpulse":
    default:             return "6 4"; // dashes
  }
}

// The score content can live inside transformed groups (scroll stage, zoom,
// drag wrappers), so all extent geometry is computed in the element's PARENT
// coordinate frame — the frame the score was authored in. The line is
// appended to that parent so it scrolls, zooms, and drags with the element.

// A "SVG-authored extent group" is a small purpose-built group wrapping the
// trigger element together with an extent line/path. Arbitrary large drawing
// groups must NOT be promoted to — their bbox spans much of the score, which
// makes the region permanently "inside" and the enter trigger unreachable.
export function extentFrameOf(element) {
  const svg = element.ownerSVGElement;
  if (!svg) return null;
  const parent = element.parentElement instanceof SVGElement ? element.parentElement : svg;
  const pctm = parent.getScreenCTM?.();
  const rctm = svg.getScreenCTM?.();
  if (!pctm || !rctm || !pctm.a || !pctm.d || !rctm.a) return null;
  return { svg, parent, pctm, rctm };
}

function _buildDslExtentLine(element, ast, frame) {
  const { parent, pctm } = frame;
  const r = element.getBoundingClientRect();
  if (!r.width && !r.height) return null;

  const rightW = (r.right - pctm.e) / pctm.a;
  const yMid   = ((r.top + r.bottom) / 2 - pctm.f) / pctm.d;

  const NS = "http://www.w3.org/2000/svg";
  const color = element._waveformColor || "#8ac";

  // Adopt a baked extent line persisted in score.svg (written by the DSL
  // editor) instead of creating a duplicate. Its geometry is normalized to
  // the element's current bbox below.
  let g = null, line = null, endBar = null;
  const hostId = element.getAttribute("id");
  if (hostId) {
    for (const cand of parent.querySelectorAll(":scope > g[data-extent-for]")) {
      if (cand.getAttribute("data-extent-for") === hostId) { g = cand; break; }
    }
  }
  if (g) {
    const lines = g.querySelectorAll("line");
    if (lines.length >= 2) { line = lines[0]; endBar = lines[1]; }
    else { g.remove(); g = null; }
  }
  if (!g) {
    g = document.createElementNS(NS, "g");
    line = document.createElementNS(NS, "line");
    endBar = document.createElementNS(NS, "line");
    g.appendChild(line);
    g.appendChild(endBar);
  }
  g.setAttribute("class", "dsl-extent-line");
  g.setAttribute("pointer-events", "none");

  line.setAttribute("x1", rightW);
  line.setAttribute("y1", yMid);
  line.setAttribute("y2", yMid);
  line.setAttribute("stroke", color);
  line.setAttribute("stroke-opacity", "0.8");
  line.setAttribute("stroke-width", "2.5");
  line.setAttribute("stroke-dasharray", _extentDashPattern(ast?.type));

  endBar.setAttribute("y1", yMid - 10);
  endBar.setAttribute("y2", yMid + 10);
  endBar.setAttribute("stroke", color);
  endBar.setAttribute("stroke-opacity", "0.8");
  endBar.setAttribute("stroke-width", "2.5");

  // Invisible hit area over the end bar — manual extent dragging, active
  // while the DSL inspector mode is on.
  const endHit = document.createElementNS(NS, "rect");
  endHit.setAttribute("class", "dsl-extent-hit");
  endHit.setAttribute("y", yMid - 16);
  endHit.setAttribute("width", "18");
  endHit.setAttribute("height", "32");
  endHit.setAttribute("fill", "transparent");
  endHit.setAttribute("pointer-events", "all");
  endHit.style.cursor = "ew-resize";

  g.appendChild(endHit);
  if (g.parentNode !== parent) parent.appendChild(g);

  endHit.addEventListener("pointerdown", (e) => {
    if (!window.dslInspectorActive) return;
    e.preventDefault();
    e.stopPropagation();
    let extentNow = element._dslExtentW || 0;
    const move = (ev) => {
      const pctm = parent.getScreenCTM();
      if (!pctm || !pctm.a) return;
      const leftL = (element.getBoundingClientRect().left - pctm.e) / pctm.a;
      extentNow = Math.max(5, (ev.clientX - pctm.e) / pctm.a - leftL);
      applyDslExtent(element, { params: { extent: extentNow } });
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      extentNow = Math.round(extentNow * 10) / 10;
      // Dragged back to (or past) the element's right edge — clear the
      // extent and fall back to plain bounding-box triggering.
      const pctm2 = parent.getScreenCTM();
      const wLocal = pctm2?.a ? element.getBoundingClientRect().width / pctm2.a : 0;
      if (wLocal && extentNow <= wLocal + 10) {
        removeDslExtent(element);
        window.dispatchEvent(new CustomEvent("oscilla:dsl-extent-changed", {
          detail: { element, extent: null }
        }));
        return;
      }
      applyDslExtent(element, { params: { extent: extentNow } });
      // The DSL inspector persists this to score.svg (or into its open form)
      window.dispatchEvent(new CustomEvent("oscilla:dsl-extent-changed", {
        detail: { element, extent: extentNow }
      }));
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  });

  // Dash animation while voices are sounding
  let animId = null, dashOffset = 0;
  const rec = {
    g, parentEl: parent, uid: _audioCueUid(ast), active: new Set(),
    setEnd(endLocalX) {
      line.setAttribute("x2", Math.max(rightW, endLocalX));
      endBar.setAttribute("x1", endLocalX);
      endBar.setAttribute("x2", endLocalX);
      endHit.setAttribute("x", endLocalX - 9);
    },
    startAnim() {
      if (animId) return;
      const animate = () => {
        const dir = (transport.speedMultiplier ?? 1) < 0 ? 0.5 : -0.5;
        dashOffset = (dashOffset + dir + 10) % 10;
        line.setAttribute("stroke-dashoffset", dashOffset);
        animId = requestAnimationFrame(animate);
      };
      animate();
    },
    stopAnim() {
      if (animId) { cancelAnimationFrame(animId); animId = null; }
      line.setAttribute("stroke-dashoffset", "0");
    },
  };
  return rec;
}

// One global listener drives all extent-line animations off voice lifecycle
window.addEventListener("oscilla:audio", (e) => {
  const uid = e.detail?.uid, state = e.detail?.state;
  if (!uid) return;
  for (const rec of _dslExtentLines.values()) {
    if (!rec.uid) continue;
    if (uid === rec.uid || uid.startsWith(`${rec.uid}__`)) {
      if (state === "play") { rec.active.add(uid); rec.startAnim(); }
      else if (state === "stop") {
        rec.active.delete(uid);
        if (!rec.active.size) rec.stopAnim();
      }
    }
  }
});

// Serialize the extent line for baking into score.svg — the visual parts
// only (no hit rect, no animation offset). Returns null when the element
// has no active extent.
export function getDslExtentMarkup(element) {
  const rec = _dslExtentLines.get(element);
  const hostId = element.getAttribute("id");
  if (!rec || !element._dslExtentW || !hostId) return null;
  const clone = rec.g.cloneNode(true);
  clone.querySelectorAll("rect").forEach(n => n.remove());
  clone.querySelectorAll("line").forEach(n => n.removeAttribute("stroke-dashoffset"));
  clone.removeAttribute("style");
  clone.setAttribute("id", `dsl-extent-${hostId}`);
  clone.setAttribute("data-extent-for", hostId);
  return clone.outerHTML;
}

export function removeDslExtent(element) {
  delete element._dslExtentW;
  const rec = _dslExtentLines.get(element);
  if (rec) { rec.stopAnim(); rec.g.remove(); _dslExtentLines.delete(element); }
}

export function applyDslExtent(element, ast) {
  // Prune records whose elements left the DOM (score reload, re-wrap)
  for (const [el, rec] of _dslExtentLines) {
    if (!el.isConnected) { rec.stopAnim(); rec.g.remove(); _dslExtentLines.delete(el); }
  }

  // Flat params (audio family) or args-array (synth) AST styles
  const extentW = Number(ast?.params?.extent ?? ast?.extent
    ?? ast?.args?.find?.(a => a.type === "extent")?.value);
  if (!extentW || extentW <= 0) {
    removeDslExtent(element);
    return;
  }
  const frame = extentFrameOf(element);
  if (!frame) return;
  const { pctm } = frame;

  // extent:N is measured in the element's parent frame (authored score
  // units). Store the WIDTH — the region checks compute the boundary live
  // from the element's current bbox each tick, so it survives scroll, zoom,
  // dragging, and the deferred drag-position restore.
  const leftLocal = (element.getBoundingClientRect().left - pctm.e) / pctm.a;
  const endLocal = leftLocal + extentW;
  element._dslExtentW = extentW;

  let rec = _dslExtentLines.get(element);
  if (rec && rec.parentEl !== frame.parent) {
    // Element was re-wrapped — rebuild in the new frame
    rec.stopAnim(); rec.g.remove(); _dslExtentLines.delete(element); rec = null;
  }
  if (!rec) {
    rec = _buildDslExtentLine(element, ast, frame);
    if (rec) _dslExtentLines.set(element, rec);
  }
  if (rec) rec.setEnd(endLocal);

  // The DSL line supersedes any scope-drawn extent visual for this element
  // (synth scopes render a default extent line at prime time).
  if (element.id) {
    for (const sg of document.querySelectorAll(".oscilla-scope")) {
      if (sg.dataset.scopeTarget === element.id) {
        sg.querySelectorAll(".scope-extent").forEach(n => { n.style.display = "none"; });
      }
    }
  }
}

export function bindAudioCueGestures(child) {
  if (child._audioCueClickBound) return;
  child._audioCueClickBound = true;
  child.style.cursor = "pointer";
  child.style.pointerEvents = "all";
  // Tag so the transport-touch pointer-capture excludes us
  if (child.dataset) child.dataset.cueClickable = "1";

  const MOVE_THRESHOLD = 6; // px — ignore clicks that are really drags
  let downX = 0, downY = 0, moved = false;
  let longPressTimer = null, longPressFired = false;
  let lastClickTime = 0, pendingClick = null;

  const onMove = (e) => {
    if (moved) return;
    if (Math.abs(e.clientX - downX) > MOVE_THRESHOLD ||
        Math.abs(e.clientY - downY) > MOVE_THRESHOLD) moved = true;
  };

  const endGesture = () => {
    if (longPressTimer) { clearTimeout(longPressTimer); longPressTimer = null; }
    document.removeEventListener("pointermove", onMove);
  };

  child.addEventListener("pointerdown", (e) => {
    if (e.button && e.button !== 0) return;
    // DSL editor open: clicks select elements, they never play them. The
    // editor swallows pointerdown itself, but the trigger below lives on
    // pointerup, so without this a synth or sample sounded on every pick.
    if (window.dslInspectorActive) return;
    downX = e.clientX; downY = e.clientY;
    moved = false; longPressFired = false;
    document.addEventListener("pointermove", onMove);
    if (longPressTimer) clearTimeout(longPressTimer);
    longPressTimer = setTimeout(() => {
      const ast = child._audioCueAst;
      if (!moved && ast && ast.type !== "cueVideo" && _audioCueIsPlayingAnywhere(ast)) {
        longPressFired = true;
        _audioCueStopFromGesture(ast, child);
      }
    }, _AUDIO_GESTURE_LONG_PRESS_MS);
  });

  child.addEventListener("pointercancel", endGesture);

  child.addEventListener("pointerup", (e) => {
    endGesture();
    if (window.dslInspectorActive) return;   // editor open — select, don't play
    const ast = child._audioCueAst;
    if (!ast || moved) return;
    e.stopPropagation();

    if (longPressFired) { lastClickTime = 0; return; }

    const fire = () => {
      if (_isNetworkedAudioCue(ast)) { _fireNetworkedAudioCue(ast, child); return; }
      child._clickTriggered = true;
      handleCueTrigger(ast, false, true, child);
      child._clickTriggered = false;
    };

    // Video: plain immediate fire, no audio gestures
    if (ast.type === "cueVideo") { fire(); return; }

    const now = Date.now();
    const isDoubleClick = (now - lastClickTime) < _AUDIO_GESTURE_DOUBLE_CLICK_MS;
    const playing = _audioCueIsPlayingAnywhere(ast);

    if (isDoubleClick && playing) {
      if (pendingClick) { clearTimeout(pendingClick); pendingClick = null; }
      _audioCueStopFromGesture(ast, child);
      lastClickTime = 0;
      return;
    }
    lastClickTime = now;

    // Poly/randomised audio routes to the pool engine (same rule as configToDSL)
    const p = ast.params || {};
    const isRandLike = (v) => v != null && typeof v === "object";
    const poly = p.poly ?? ast.poly ?? 1;
    const hasPolyFeatures = poly !== 1 || isRandLike(p.speed) || isRandLike(p.pan);
    const isSingleAudio = ast.type === "cueAudio" && !hasPolyFeatures;
    const isPool = ast.type === "cueAudioPool" || (ast.type === "cueAudio" && hasPolyFeatures);

    if (isPool) {
      // Immediate: no double-click delay on percussive retriggers
      if (pendingClick) { clearTimeout(pendingClick); pendingClick = null; }
      if (e.shiftKey && playing) _audioCueStopOldestVoice(ast);
      else fire();
      return;
    }

    // Mono audio / impulse / synth: delay so a double-click can cancel
    if (pendingClick) clearTimeout(pendingClick);
    pendingClick = setTimeout(() => {
      pendingClick = null;
      const uid = _audioCueUid(ast);
      // Sounding only on other clients (a networked click): this click
      // stops it there — there is nothing here to pause
      if (_isNetworkedAudioCue(ast) && !_audioCueIsPlaying(ast) && _audioCuePlayingElsewhere(uid)) {
        _stopNetworkedAudioCue(ast, child);
        return;
      }
      if (isSingleAudio) {
        const voice = uid ? _audioCueVoices(uid)[0]?.[1] : null;
        if (!voice) { fire(); return; }
        if (typeof voice.isPaused === "function" && voice.isPaused()) voice.resume?.();
        else voice.pause?.();
      } else if (_isNetworkedAudioCue(ast) && _audioCueIsPlaying(ast)) {
        // A running impulse / synth: the click that would toggle it off
        // here toggles it off everywhere
        _stopNetworkedAudioCue(ast, child);
      } else {
        fire();
      }
    }, _AUDIO_GESTURE_DOUBLE_CLICK_MS);
  });
}

// =============================================================
//  Dynamic Cue Registration
//
//  Register/unregister SVG elements added after assignCues(),
//  e.g. code that injects <rect> elements with DSL IDs.
//  Uses the same parseCueToAST, CUE_START_RE
//  and overlay priming that assignCues uses.
// =============================================================

/**
 * Register a single SVG element as a cue.
 *
 * The element's `id` must be a valid DSL expression, e.g.:
 *   audio(src:noise/test.wav, amp:0.8, uid:trigger-ao_123)
 *
 * Parses DSL, primes overlays and waveforms, adds to window.cues[]
 * so the playhead trigger system (checkCueTriggers) picks it up.
 *
 * @param {SVGElement} element - SVG element whose id is a DSL expression
 * @returns {object|null} cue entry added to window.cues[], or null
 */
export function registerSingleCue(element) {
  if (!element) return null;

  const dataOscilla = element.getAttribute?.('data-oscilla');
  const rawExpr = (dataOscilla && CUE_START_RE.test(dataOscilla)) ? dataOscilla : element.id;
  const cueExpr = rawExpr?.trim();

  if (!cueExpr || !CUE_START_RE.test(cueExpr)) {
    console.warn("[registerSingleCue] No valid cue expression:", element.id);
    return null;
  }

  let ast;
  try {
    ast = parseCueToAST(cueExpr);
  } catch (err) {
    console.warn("[registerSingleCue] Parse failed:", cueExpr, err);
    return null;
  }

  if (!ast) return null;

  // Its HTML overlays — cue button, click:1, drag — were built from the DSL
  // it had at load. Build them from what it says now, or a button added in
  // the editor sits in the file and does nothing until a reload.
  rebuildOverlaysFor(element);
  // A button is not a playhead cue: the load walk skips it, and an entry
  // here would fire its trigger when the playhead crossed the shape.
  if (ast.type === "cueButton") return null;

  // Refresh this element's connection-graph edges: its old ones describe DSL
  // that may have just been rewritten, and re-collecting only THIS element is
  // what lets an edit skip the whole-score rescan.
  dropRefsFor(element);
  dropEdgesFor(element);
  collectGraphEdges(element, cueExpr, ast);

  // trig:"adc.onset>0.5" / trig:midi — a bus watcher keyed by the element, as
  // in the assignCues walk: the old one goes with the old DSL, a new one is
  // made if the DSL still has one, and the cue stays off the playhead list.
  dropSignalTrigger(element);
  const _sigTrig = midiTrigOf(ast, ast.args?.find(a => a.type === "trig")?.value ?? ast.params?.trig ?? ast.trig);
  if (_sigTrig !== null && isSignalTrig(_sigTrig)) {
    const onceVal = ast.args?.find(a => a.type === "once")?.value ?? ast.params?.once ?? ast.once;
    const once = onceVal === 1 || onceVal === "1" || onceVal === true;
    const step = patternStepper();
    registerSignalTrigger(String(_sigTrig), () => handleCueTrigger(step(ast), false, true, element),
                          { key: element, once });
    const _i = window.cues?.findIndex(c => c.element === element) ?? -1;
    if (_i >= 0) window.cues.splice(_i, 1);
    return null;
  }

  // Click gestures for audio-type cues (same as the assignCues walk) —
  // set/refresh the AST first so an updated cue uses its new params.
  const _clickableTypes = ["cueAudio", "cueAudioPool", "cueAudioImpulse", "cueSynth", "cueVideo", "cueImage"];
  if (_clickableTypes.includes(ast.type)) {
    const clickParam = _audioCueParam(ast, "click");
    const clickDisabled = clickParam === 0 || clickParam === "0" || clickParam === false;
    if (!clickDisabled) {
      element._audioCueAst = ast;
      bindAudioCueGestures(element);
    }
    applyDslExtent(element, ast);
  }

  // Standalone pin() — register directly, same as the assignCues walk.
  // Pins are driven by the RAF pipeline, not the playhead trigger system,
  // so no cue entry is pushed.
  if (ast.type === "cuePin") {
    if (ast.release) registerPinRelease(element, ast.release);
    else registerPin(element, ast.dur, ast);
    return null;
  }

  // --- (Re)register fade priming, same as the assignCues walk ---
  // Must run before the duplicate-registration early return so an edited
  // fade() re-primes with its new params.
  if (ast.type === "cueFade") {
    window._fadeCues ??= new Map();
    window._fadeCues.set(element, ast);
    primeFadeTargetFromAST(ast, element);
  }

  // image(): show the first picture before the cue fires, so a score at rest
  // reads as the score rather than a row of empty boxes
  if (ast.type === "cueImage") {
    import("./image.js").then(mod => mod.primeImage(ast, element));
  }

  // --- Avoid duplicate registration ---
  if (!window.cues) window.cues = [];
  const existing = window.cues.find(c => c.element === element);

  // A sound with trig:click / manual / none is not the playhead's (same rule
  // as the assignCues walk): primed and clickable, but off the cue list —
  // and taken off it if an edit just made it so.
  if (_AUDIO_GESTURE_TYPES.includes(ast.type)) {
    const _trigLc = String(_audioCueParam(ast, "trig") ?? "").toLowerCase();
    if (_trigLc === "click" || _trigLc === "manual" || _trigLc === "none") {
      if (existing) window.cues.splice(window.cues.indexOf(existing), 1);
      _primeAudioFamily(ast, element);
      return null;
    }
  }

  if (existing) {
    existing.id = cueExpr;
    existing.ast = ast;
    existing.triggered = false;
    return existing;
  }

  _primeAudioFamily(ast, element);

  // --- Build cue entry (same shape as assignCues produces) ---
  // Where this cue lives, in world units — the space window.playheadX is
  // measured in. A screen x is a property of this viewport, not of the score.
  let homeWorldX = null;
  {
    const _svg = element.ownerSVGElement;
    const _ctm = _svg?.getScreenCTM?.();
    if (_ctm && _ctm.a !== 0) homeWorldX = (element.getBoundingClientRect().left - _ctm.e) / _ctm.a;
  }

  const cueEntry = {
    id: cueExpr,
    // Per-element trigger state, as in assignCues. A dynamically registered
    // cue is one element, so its own id is already unique to it unless the
    // same expression is also in the score — in which case the element
    // identity is what keeps them apart.
    key: `${cueExpr}#dyn${(window.cues?.length ?? 0) + 1}`,
    ast,
    element,
    // Same world measurement assignCues takes, so a cue registered at runtime
    // has a position in the units everything downstream expects.
    homeWorldX,
    triggered: false,
    _dynamicCue: true,
  };

  window.cues.push(cueEntry);

  // Generic pin: any cue with pin:N gets pinned to playhead
  const pinDur = ast.params?.pin ?? ast.pin ?? ast.args?.find(a => a.type === 'pin')?.value;
  if (pinDur != null && pinDur > 0) {
    registerPin(element, Number(pinDur));
  }

  return cueEntry;
}

/**
 * Remove a cue by its SVG element reference.
 * Removes from window.cues[] only — does NOT remove the SVG element
 * or destroy waveform/overlay DOM (caller handles that).
 *
 * @param {SVGElement} element
 * @returns {boolean} true if found and removed
 */
export function unregisterSingleCue(element) {
  if (!element) return false;

  removeDslExtent(element);
  destroyOverlaysFor(element);
  stopDrawForElement(element);
  dropSignalTrigger(element);

  // Drop fade priming: otherwise the element keeps its primed opacity and
  // gets re-hidden on every rewind even after its fade() DSL is gone.
  forgetFadeTarget(element);

  if (!window.cues) return false;

  const idx = window.cues.findIndex(c => c.element === element);
  if (idx < 0) return false;

  // Tear down audio-family voices and visuals. Without this, removing a
  // cue's DSL leaves its waveform/scope on screen (and any playing voices
  // sounding) until a full page reload.
  const ast = window.cues[idx].ast;
  try {
    const uid = ast?.type === "cueSynth" ? _cueSynthUid(ast, element) : _audioCueUid(ast);
    if (uid) {
      // Stop playing voices: audio/audioPool registry, impulse process, synth
      const reg = window.activeAudioCues;
      if (reg) {
        for (const [k, voice] of [...reg.entries()]) {
          if (k === uid || k.startsWith(`${uid}__`)) {
            try { voice?.stop?.(0.15); } catch { /* ignore */ }
            reg.delete(k);
          }
        }
      }
      if (window.audioImpulses?.has(uid)) stopAudioImpulse(uid, 0.15);
      if (ast?.type === "cueSynth") {
        try { stopSynth(uid); } catch { /* ignore */ }
      }
      if (ast?.type === "cueMetronome") {
        try { stopMetro(uid); } catch { /* ignore */ }
      }
      // Destroy waveform boxes and synth scopes (incl. poly sub-voice
      // handles) — scope handles live in the same registry as scope-<uid>
      for (const prefix of [uid, `scope-${uid}`]) {
        for (const h of getWaveformsByPrefix(prefix)) {
          try { destroyWaveform(h.uid); } catch { /* ignore */ }
        }
      }
    }
  } catch { /* best-effort cleanup */ }
  element._waveformPrimed = false;
  element._synthScopePrimed = false;

  window.cues.splice(idx, 1);
  return true;
}

// Overlays and waveforms for a sound cue (registerSingleCue)
function _primeAudioFamily(ast, element) {
  if (ast.type === "cueAudio") {
    const wfParam = ast.waveform ?? ast.params?.waveform ?? "self";
    if (wfParam === "none" || wfParam === "0") {
      primeAudioOverlay(ast, element);
    }
    primeWaveform(ast, element);
  }

  if (ast.type === "cueAudioPool") {
    primeAudioPoolOverlay(ast, element);
    primePoolWaveform(ast, element);
  }

  if (ast.type === "cueAudioImpulse") {
    primeAudioImpulseOverlay(ast, element);
    primeImpulseWaveform(ast, element);
  }

  if (ast.type === "cueSynth") {
    primeSynthOverlay(ast, element);
  }
}

/////////////////////////////////////////////////////////

export { splitCueId } from "../parser/cueUtils.js";
import { readTdelay } from "../parser/cueUtils.js";
import { secs } from "./beats.js";
import { settleRandom } from "../interaction/liveEval.js";
import { seededRandom } from "../system/seededRandom.js";
import { bindingsFrom } from "../control/paramBinding.js";
import { expandGroups, handleGroupCue } from "./groups.js";
import { expandStyle, handleStyleCue } from "./textStyles.js";
import { isRel, applyRel, hasRel } from "./relative.js";
import { transport, setElapsed } from "../system/transportState.js";

/**
 * assignCues(svgRoot)
 * ---------------------
 * Finds all <g> elements with ID format:
 *   <g id="assignCues(cueOscTrigger(rnd[1,9]))">
 *   <g id="assignCues(cueOscSet(speed, ypos[0.5,1.5]))">
 *
 * Assigns cue IDs to each child based on:
 *   - rnd[min,max]     → random float value
 *   - ypos[min,max]    → scaled vertical position
 * Also walks the entire SVG tree to catch other cue(...) elements.
 */

// Inject a tiny stylesheet (once) for the notehead "fired" flash.
// Triggered both by user click and by playhead-pass.
(function _injectNotationFireCSS() {
  if (typeof document === "undefined") return;
  if (document.getElementById("oscilla-notation-fire-css")) return;
  const s = document.createElement("style");
  s.id = "oscilla-notation-fire-css";
  // Keep the flash CHEAP — no CSS filter. drop-shadow on an SVG <g>
  // promotes the entire SVG into a compositing layer for the duration
  // of the transition; on every click that layer is created and then
  // destroyed, which the browser surfaces as a brief score-wide
  // re-paint ("lensing wobble"). A simple fill / stroke colour swap
  // on the fired notehead's children stays within that one element.
  s.textContent = `
    .notation-note.notation-note-fired text { fill: #ff3b00 !important; }
    .notation-note.notation-note-fired ellipse,
    .notation-note.notation-note-fired path { stroke: #ff3b00 !important; }
  `;
  document.head.appendChild(s);
})();

// Document-level capture-phase pointerdown/up listener.
//
// We trigger off pointerdown (or pointerup as a fallback) rather than
// the synthetic `click` event because:
//   1. HTML overlays over the score frequently call stopPropagation,
//      shadowing per-note bubble-phase listeners.
//   2. Browsers only synthesize a `click` if mousedown and mouseup share
//      the same target. Tiny cursor drift between press/release, or any
//      DOM mutation that removes the original target, breaks that.
//
// Pointerdown at capture phase always sees the target we actually pressed
// on, regardless of drift or downstream stopPropagation. We mark the
// event so per-note listeners further along don't double-fire.
(function _installNotationDocumentPointerFallback() {
  if (typeof document === "undefined") return;
  if (window._notationDocClickFallbackInstalled) return;
  window._notationDocClickFallbackInstalled = true;

  function _hitNotationNote(target) {
    let n = target;
    for (let depth = 0; n && depth < 6; depth++, n = n.parentNode) {
      if (n.classList && n.classList.contains("notation-note")) return n;
    }
    return null;
  }

  // Track a pending fire from pointerdown so we don't double-fire on
  // pointerup. Keyed by pointerId.
  const _pendingFires = new Map();

  document.addEventListener("pointerdown", (ev) => {
    if (ev.button && ev.button !== 0) return;
    if (isNotationEditing()) return;
    const n = _hitNotationNote(ev.target);
    if (!n) return;
    if (typeof n._notationOscFire !== "function") return;
    const idxAttr = Number.parseInt(n.getAttribute("data-note-index"), 10);
    const idx = Number.isFinite(idxAttr) ? idxAttr : 0;
    n._notationOscFire(idx, "click");
    _pendingFires.set(ev.pointerId, true);
    ev._notationFired = true;
    // NOTE: do NOT stopPropagation here. Some parent handlers
    // preventDefault on pointerdown to suppress the browser's native
    // scroll/touch-pan; stopping propagation lets that default through
    // and produces a "lensing wobble" of the entire score. We rely on
    // ev._notationFired and the click-capture suppressor to dedup.
  }, { capture: true });

  // Suppress the synthetic click for the same pointer so per-note
  // bubble click listeners don't refire. Click capture phase runs AFTER
  // pointerup capture, so we must not clear _pendingFires on pointerup
  // — otherwise the click handler would see no pending fire and fail to
  // mark/dedup. Click capture is the natural place to clear.
  document.addEventListener("click", (ev) => {
    if (_pendingFires.size > 0) {
      ev._notationFired = true;
      _pendingFires.clear();
    }
  }, { capture: true });

  document.addEventListener("pointercancel", (ev) => {
    _pendingFires.delete(ev.pointerId);
  }, { capture: true });
})();

/**
 * Resolve effective click / playhead OSC flags for a single note.
 * Both default to `true` so existing cues without any stored override
 * fire on click and on playhead-pass. Per-note overrides (if set) win
 * over the stave-level default; stave default wins over the bare
 * default.
 *
 * For multi-stave system cues, pass staveIdx so the lookup reads from
 * `entry.stave_<sIdx>` instead of the top-level entry.
 */
function _resolveNoteOscFlags(uid, i, staveIdx /* number | null */) {
  const entry = window._notationPositions?.[uid];
  const scope = (staveIdx == null)
    ? (entry || {})
    : ((entry?.[`stave_${staveIdx}`]) || {});
  const noOverrideClick    = scope.oscClickOverrides?.[i];
  const noOverridePlayhead = scope.oscPlayheadOverrides?.[i];
  const click = (typeof noOverrideClick === "boolean")
    ? noOverrideClick
    : (typeof scope.oscClickDefault === "boolean" ? scope.oscClickDefault : true);
  const playhead = (typeof noOverridePlayhead === "boolean")
    ? noOverridePlayhead
    : (typeof scope.oscPlayheadDefault === "boolean" ? scope.oscPlayheadDefault : true);
  return { click, playhead };
}

/** Brief visual flash on a note <g> indicating an OSC fire just occurred. */
function _flashNoteFire(noteEl) {
  if (!noteEl) return;
  noteEl.classList.add("notation-note-fired");
  clearTimeout(noteEl._notationFireTimer);
  noteEl._notationFireTimer = setTimeout(() => {
    noteEl.classList.remove("notation-note-fired");
  }, 220);
}

/**
 * Trigger a per-note sample assignment via the existing audio-cue path.
 * Builds a one-shot AST with a unique uid per fire so the audio engine
 * doesn't apply its mono-voice live-update behaviour. `noteUid` is the
 * cue's uid; `noteIdx` participates in the playback uid so different
 * notes don't stomp on each other's voices.
 */
function _fireNoteSample(noteUid, noteIdx, sample, source = "click") {
  if (!sample) return;
  const src = typeof sample === "string" ? sample : sample.src;
  if (!src) return;
  // handleAudioCue's fetcher already tries a picked folder, /scores/<project>/audio/<src>,
  // /shared/audio/<src>. Pass the relative path (the
  // value the picker stored) — prefixing here would cause the project
  // root to appear twice in the URL.
  const playUid = `notation-${noteUid}-${noteIdx}-${Date.now().toString(36)}`;
  try {
    handleAudioCue({
      src,
      uid: playUid,
      amp: typeof sample.gain === "number" ? sample.gain : 1,
      speed: typeof sample.speed === "number" ? sample.speed : 1,
      poly: 0,    // unlimited overlap so rapid retriggers don't cancel
    });
  } catch (err) {
    console.warn("[notation sample fire] handleAudioCue failed:", err);
  }
}

/**
 * Resolve the position-bbox of a notation host element. Prefers getBBox()
 * for hosts with rendered geometry, but falls back to the x/y attributes
 * for invisible host rects (fill=none stroke=none) where some browsers
 * return an empty bbox even though x/y are set — without this fallback the
 * rendered notation group would sit at the layer origin (0,0) instead of
 * the cue's authored position.
 */
function _resolveHostBbox(child) {
  const bb = child.getBBox();
  if (bb.width || bb.height) return bb;
  return {
    x: parseFloat(child.getAttribute("x")) || 0,
    y: parseFloat(child.getAttribute("y")) || 0,
    width: 0,
    height: 0,
  };
}

/**
 * Render a notation() cue on a host element. Extracted from assignCues so
 * session-created cues (created live via the topbar create-mode tool) can
 * use the same rendering path. Idempotent — re-invoking on the same host
 * replaces the previous render.
 *
 * @param {Element} child - host SVG element (rect/circle/etc with bbox)
 * @param {object}  ast   - { type:"cueNotation", params:{...} }
 */
export function renderNotationCue(child, ast) {
  const p = ast?.params || {};
  const pitchStr = p.pitches || p.pitch || "";
  const pitches = pitchStr.split(/\s+/).filter(Boolean);
  if (!pitches.length) return;
  try {
    const uid = p.uid
      || child.getAttribute("data-notation-uid")
      || `notation_${child.id || "anon"}_${pitches.length}`;
    child.setAttribute("data-notation-uid", uid);

    const baseOpts = {};
    if (p.transp || p.transposition) baseOpts.transposition = p.transp || p.transposition;
    if (p.clef) baseOpts.clef = p.clef;
    if (p.keysig || p.key) baseOpts.keysig = p.keysig || p.key;
    if (p.height || p.staffHeight) baseOpts.staffHeight = Number(p.height || p.staffHeight);
    if (p.color || p.colour) baseOpts.color = p.color || p.colour;
    if (p.spacing) baseOpts.noteSpacing = Number(p.spacing);
    if (p.box || p.boxed) baseOpts.boxed = !!(p.box ?? p.boxed);
    if (p.boxRadius != null) baseOpts.boxRadius = Number(p.boxRadius);
    // OSC plumbing: the render reads these from opts (oscRoot, click/playhead
    // enables) — without copying them the DSL's osc:/oscaddr:/click:/playhead:
    // would be ignored and every host use the default address.
    if (p.oscaddr != null) baseOpts.oscaddr = p.oscaddr;
    if (p.osc != null) baseOpts.osc = p.osc;
    if (p.click != null) baseOpts.click = p.click;
    if (p.playhead != null) baseOpts.playhead = p.playhead;

    const bbox = _resolveHostBbox(child);
    const render = (overrides = {}) => {
      if (child._notationGroup) {
        child._notationGroup.remove();
        child._notationGroup = null;
        if (child._dragCompanions) {
          child._dragCompanions = child._dragCompanions.filter(c => c.isConnected);
        }
      }
      let activePitches = pitches;
      let activeBase = baseOpts;
      const saved = window._notationPositions?.[uid] || null;
      const dslOverride = saved?.dslOverride;
      if (dslOverride) {
        try {
          const parsedOverride = parseCueParams(dslOverride);
          if (parsedOverride?.type === "cueNotation") {
            const op = parsedOverride.params || {};
            const opStr = op.pitches || op.pitch || "";
            const opPitches = opStr.split(/\s+/).filter(Boolean);
            if (opPitches.length) activePitches = opPitches;
            const oo = {};
            if (op.transp || op.transposition) oo.transposition = op.transp || op.transposition;
            if (op.clef) oo.clef = op.clef;
            if (op.keysig || op.key) oo.keysig = op.keysig || op.key;
            if (op.height || op.staffHeight) oo.staffHeight = Number(op.height || op.staffHeight);
            if (op.color || op.colour) oo.color = op.color || op.colour;
            if (op.spacing) oo.noteSpacing = Number(op.spacing);
            if (op.box || op.boxed) oo.boxed = !!(op.box ?? op.boxed);
            if (op.boxRadius != null) oo.boxRadius = Number(op.boxRadius);
            activeBase = { ...baseOpts, ...oo };
          }
        } catch (err) {
          console.warn(`[notation ${uid}] dslOverride parse failed:`, err);
        }
      }

      // Pull persisted edit-mode overrides into THIS render call so any
      // _notationRender({}) (e.g. after a paste, where editState was just
      // restored) picks up the saved offsets / extent / styles. Pass-in
      // overrides still win, since edit-mode drag callsites use them for
      // live previews that override saved state.
      const savedOverrides = {};
      if (saved?.noteOffsets)    savedOverrides.noteOffsets    = saved.noteOffsets;
      if (saved?.staffWidth != null) savedOverrides.staffWidth = saved.staffWidth;
      if (saved?.noteheadStyles) savedOverrides.noteheadStyles = saved.noteheadStyles;
      if (saved?.extentX != null) savedOverrides.extentX       = saved.extentX;
      if (saved?.extentStyle)    savedOverrides.extentStyle    = saved.extentStyle;
      if (Array.isArray(saved?.glisses)) savedOverrides.glisses = saved.glisses;
      if (saved?.articulations && typeof saved.articulations === "object") {
        savedOverrides.articulations = saved.articulations;
      }
      if (saved?.dynamics && typeof saved.dynamics === "object") {
        savedOverrides.dynamics = saved.dynamics;
      }

      const opts = { ...activeBase, ...savedOverrides, ...overrides };
      const g = createNotation(activePitches, opts);
      g.setAttribute("transform", `translate(${bbox.x}, ${bbox.y})`);
      g.classList.add("oscilla-notation-host-rendered");
      g.setAttribute("data-notation-uid", uid);
      child.parentNode.insertBefore(g, child.nextSibling);
      g.style.pointerEvents = "none";
      g._dragBaseTransform = `translate(${bbox.x}, ${bbox.y})`;
      if (window._dragRegistry) {
        (child._dragCompanions ||= []).push(g);
        for (const entry of window._dragRegistry.values()) {
          if (entry?.el === child && (entry.dx || entry.dy)) {
            g.setAttribute(
              "transform",
              `translate(${entry.dx}, ${entry.dy}) translate(${bbox.x}, ${bbox.y})`
            );
            break;
          }
        }
      }
      child._notationGroup = g;

      // OSC plumbing (click + playhead-pass)
      const oscRoot = (typeof opts.oscaddr === "string"
        ? opts.oscaddr
        : (typeof opts.osc === "string" ? opts.osc : null))
        || `/oscilla/notation/${uid}/note`;
      const oscClickEnabled = opts.osc !== 0 && opts.osc !== false && opts.click !== 0 && opts.click !== false;
      // Playhead loop is always armed; the per-note / stave-level flags
      // (resolved by _resolveNoteOscFlags) are the real gate, so a cue
      // without playhead:1 fires on playhead-pass by default — overridable
      // via the popup's Audio tab.

      const fireNoteOsc = (i, payload, noteEl, source = "click") => {
        // Respect per-note / stave-level OSC enable flags. If click or
        // playhead is disabled for this note, the entire fire is a
        // no-op for that source.
        const flags = _resolveNoteOscFlags(uid, i, null);
        if (source === "click"    && !flags.click)    return;
        if (source === "playhead" && !flags.playhead) return;

        const styleNow =
          window._notationPositions?.[uid]?.noteheadStyles?.[i] ?? "filled";
        let addr = oscRoot;
        if (typeof addr === "string" && addr.includes("{i}")) {
          addr = addr.replace(/\{i\}/g, String(i));
        }
        sendOSC({
          type: "osc_trigger",
          addr,
          args: [
            i,
            Number(payload.midi.toFixed(4)),
            Number(payload.freq.toFixed(4)),
            payload.cents | 0,
            String(styleNow),
            String(payload.pitchName),
          ],
        }, { immediate: true });
        _flashNoteFire(noteEl);
        // Per-note sample (single-stave) — set via the popup's Audio tab.
        const sample = window._notationPositions?.[uid]?.samples?.[i];
        if (sample) _fireNoteSample(uid, i, sample, source);
      };

      const noteEls = g.querySelectorAll(".notation-note");
      noteEls.forEach((noteEl, i) => {
        const sounding = parsePitch(activePitches[i]);
        if (!sounding) return;
        const midi = pitchToMidi(sounding);
        const freq = midiToFreq(midi);
        const pitchName = formatPitchName(sounding);
        const cents = sounding.cents || 0;
        noteEl._notationOscPayload = { uid, index: i, midi, freq, cents, pitchName };
        noteEl._notationOscFire = (idx, src = "playhead") =>
          fireNoteOsc(idx, noteEl._notationOscPayload, noteEl, src);
        noteEl._notationPlayheadEnabled = true;
        if (oscClickEnabled) {
          noteEl.style.cursor = "pointer";
          noteEl.addEventListener("click", (ev) => {
            if (isNotationEditing()) return;
            // Doc-level capture handler may already have fired this.
            if (ev._notationFired) return;
            ev.stopPropagation();
            fireNoteOsc(i, { midi, freq, cents, pitchName }, noteEl, "click");
          });
        }
      });

      // Gliss → OSC (phase 7e)
      const glissList = activeBase.glisses;
      if (Array.isArray(glissList)) {
        const fireGlissOsc = (gl, sEl, dEl) => {
          const sPitch = parsePitch(activePitches[gl.from.index]);
          const dPitch = parsePitch(activePitches[gl.to.index]);
          if (!sPitch || !dPitch) return;
          const fromMidi = pitchToMidi(sPitch);
          const toMidi   = pitchToMidi(dPitch);
          const fromFreq = midiToFreq(fromMidi);
          const toFreq   = midiToFreq(toMidi);
          const fromName = formatPitchName(sPitch);
          const toName   = formatPitchName(dPitch);
          const sCx = parseFloat(sEl.getAttribute("data-cx"));
          const dCx = parseFloat(dEl.getAttribute("data-cx"));
          const dxPx = Math.abs(dCx - sCx);
          let addr = oscRoot.replace(/\/note$/, "/gliss");
          if (addr === oscRoot) addr = `${oscRoot}-gliss`;
          if (typeof addr === "string" && addr.includes("{i}")) {
            addr = addr.replace(/\{i\}/g, String(gl.from.index));
          }
          sendOSC({
            type: "osc_trigger",
            addr,
            args: [
              String(gl.id || ""),
              Number(fromMidi.toFixed(4)),
              Number(toMidi.toFixed(4)),
              Number(fromFreq.toFixed(4)),
              Number(toFreq.toFixed(4)),
              Number(dxPx.toFixed(2)),
              String(gl.style || "straight"),
              String(fromName),
              String(toName),
            ],
          }, { immediate: true });
        };
        for (const gl of glissList) {
          if (!Number.isFinite(gl?.from?.index)) continue;
          if (!Number.isFinite(gl?.to?.index)) continue;
          const sEl = g.querySelector(`.notation-note[data-note-index="${gl.from.index}"]`);
          const dEl = g.querySelector(`.notation-note[data-note-index="${gl.to.index}"]`);
          if (!sEl || !dEl) continue;
          if (!sEl._notationGlissFires) sEl._notationGlissFires = [];
          sEl._notationGlissFires.push(() => fireGlissOsc(gl, sEl, dEl));
        }
      }

      return g;
    };

    child._notationRender = render;
    child._notationPitches = pitches;
    child._notationBaseOpts = baseOpts;

    // render() reads saved state itself, so {} is sufficient here.
    render();
  } catch (err) {
    console.error("[renderNotationCue] failed:", err);
  }
}

/**
 * Render a multi-stave notation system on a host element. Used by
 * notationCreate.js for system-mode session cues (piano, organ, quartet,
 * custom). Idempotent — calling again replaces the previous render.
 *
 * @param {Element} child - host SVG element with bbox positioning
 * @param {Object}  spec  - { staves:[{pitches, clef, transp, keysig, ...}],
 *                            staveGap, glisses, color, staffHeight, ...,
 *                            uid, oscaddr, playhead }
 */
export function renderNotationSystemCue(child, spec) {
  if (!spec || !Array.isArray(spec.staves) || spec.staves.length === 0) return;
  try {
    const uid = spec.uid
      || child.getAttribute("data-notation-uid")
      || `system_${child.id || "anon"}_${spec.staves.length}`;
    child.setAttribute("data-notation-uid", uid);
    child.setAttribute("data-notation-kind", "system");

    const bbox = _resolveHostBbox(child);

    const render = (overrides = {}) => {
      if (child._notationGroup) {
        child._notationGroup.remove();
        child._notationGroup = null;
        if (child._dragCompanions) {
          child._dragCompanions = child._dragCompanions.filter(c => c.isConnected);
        }
      }

      // Pull persisted edit-mode overrides into the system spec
      const saved = window._notationPositions?.[uid] || {};

      // Migration: older system cues stored extentX per-stave. If we see
      // any per-stave extentX and the new system-level field is unset,
      // promote the max of the per-stave values so the cue still renders
      // an extent. (Per-stave extentX is no longer drawn at the renderer
      // level when systemExtentX is set.)
      if (saved.systemExtentX == null) {
        let maxPerStave = null;
        for (let i = 0; i < spec.staves.length; i++) {
          const ps = saved[`stave_${i}`];
          if (ps?.extentX != null && ps.extentX > 0) {
            maxPerStave = Math.max(maxPerStave ?? 0, ps.extentX);
          }
        }
        if (maxPerStave != null) {
          saved.systemExtentX = maxPerStave;
        }
      }

      const sysOpts = {
        ...spec,
        ...overrides,
        staveGap: overrides.staveGap ?? saved.staveGap ?? spec.staveGap ?? 30,
        glisses:  overrides.glisses  ?? saved.glisses  ?? spec.glisses  ?? null,
        // System-level extent + box (shared across all staves)
        extentX:     overrides.systemExtentX     ?? saved.systemExtentX     ?? spec.extentX     ?? null,
        extentStyle: overrides.systemExtentStyle ?? saved.systemExtentStyle ?? spec.extentStyle ?? "block",
        boxed:       overrides.systemBoxed       ?? saved.systemBoxed       ?? spec.boxed       ?? false,
      };
      // Per-stave overrides applied from saved (stave_<i> keys)
      sysOpts.staves = spec.staves.map((staveOpts, i) => {
        const perStave = saved[`stave_${i}`] || {};
        return {
          ...staveOpts,
          noteOffsets:    overrides[`stave_${i}_noteOffsets`]    ?? perStave.noteOffsets    ?? staveOpts.noteOffsets    ?? null,
          staffWidth:     overrides[`stave_${i}_staffWidth`]     ?? perStave.staffWidth     ?? staveOpts.staffWidth     ?? null,
          noteheadStyles: overrides[`stave_${i}_noteheadStyles`] ?? perStave.noteheadStyles ?? staveOpts.noteheadStyles ?? null,
          extentX:        overrides[`stave_${i}_extentX`]        ?? perStave.extentX        ?? staveOpts.extentX        ?? null,
          extentStyle:    overrides[`stave_${i}_extentStyle`]    ?? perStave.extentStyle    ?? staveOpts.extentStyle    ?? "block",
          articulations:  overrides[`stave_${i}_articulations`]  ?? perStave.articulations  ?? staveOpts.articulations  ?? null,
          dynamics:       overrides[`stave_${i}_dynamics`]       ?? perStave.dynamics       ?? staveOpts.dynamics       ?? null,
        };
      });

      const g = createNotationSystem(sysOpts);
      g.setAttribute("transform", `translate(${bbox.x}, ${bbox.y})`);
      g.classList.add("oscilla-notation-host-rendered");
      g.setAttribute("data-notation-uid", uid);
      child.parentNode.insertBefore(g, child.nextSibling);
      g.style.pointerEvents = "none";
      g._dragBaseTransform = `translate(${bbox.x}, ${bbox.y})`;
      if (window._dragRegistry) {
        (child._dragCompanions ||= []).push(g);
        for (const entry of window._dragRegistry.values()) {
          if (entry?.el === child && (entry.dx || entry.dy)) {
            g.setAttribute(
              "transform",
              `translate(${entry.dx}, ${entry.dy}) translate(${bbox.x}, ${bbox.y})`
            );
            break;
          }
        }
      }
      child._notationGroup = g;

      // OSC: per-stave click handlers (each stave's notes carry their own
      // sounding pitches). Address pattern: /oscilla/notation/<uid>/note,
      // with literal {s} → stave index and {i} → note index in the path.
      const oscRoot = (typeof sysOpts.oscaddr === "string" ? sysOpts.oscaddr : null)
        || `/oscilla/notation/${uid}/note`;
      const oscClickEnabled = sysOpts.osc !== 0 && sysOpts.osc !== false && sysOpts.click !== 0 && sysOpts.click !== false;

      g.querySelectorAll(".oscilla-notation-stave").forEach((staveG) => {
        const sIdx = parseInt(staveG.getAttribute("data-stave-index"), 10);
        const stavePitches = sysOpts.staves[sIdx]?.pitches || [];
        staveG.querySelectorAll(".notation-note").forEach((noteEl, i) => {
          const sounding = parsePitch(stavePitches[i]);
          if (!sounding) return;
          const midi = pitchToMidi(sounding);
          const freq = midiToFreq(midi);
          const pitchName = formatPitchName(sounding);
          const cents = sounding.cents || 0;
          noteEl._notationOscPayload = { uid, staveIdx: sIdx, index: i, midi, freq, cents, pitchName };
          // Signature: (_idxIgnored, src) — same shape as single-stave so
          // playhead-pass dispatch (`fire(payload.index)`) doesn't pass the
          // numeric index in as the source string.
          noteEl._notationOscFire = (_idxIgnored, src = "playhead") => {
            // Respect per-note / per-stave OSC enable flags.
            const flags = _resolveNoteOscFlags(uid, i, sIdx);
            if (src === "click"    && !flags.click)    return;
            if (src === "playhead" && !flags.playhead) return;

            let addr = oscRoot;
            if (typeof addr === "string") {
              if (addr.includes("{s}")) addr = addr.replace(/\{s\}/g, String(sIdx));
              if (addr.includes("{i}")) addr = addr.replace(/\{i\}/g, String(i));
            }
            sendOSC({
              type: "osc_trigger",
              addr,
              args: [
                sIdx, i,
                Number(midi.toFixed(4)),
                Number(freq.toFixed(4)),
                cents | 0,
                String(pitchName),
              ],
            }, { immediate: true });
            _flashNoteFire(noteEl);
            // Per-note sample (system / multi-stave) — set via the
            // popup's Audio tab; persisted under entry.stave_<sIdx>.samples.
            const sample = window._notationPositions?.[uid]?.[`stave_${sIdx}`]?.samples?.[i];
            if (sample) _fireNoteSample(`${uid}-s${sIdx}`, i, sample, src);
          };
          noteEl._notationPlayheadEnabled = true;
          if (oscClickEnabled) {
            noteEl.style.cursor = "pointer";
            noteEl.addEventListener("click", (ev) => {
              if (isNotationEditing()) return;
              if (ev._notationFired) return;
              ev.stopPropagation();
              noteEl._notationOscFire(null, "click");
            });
          }
        });
      });

      return g;
    };

    child._notationRender = render;
    child._notationKind = "system";
    child._notationSpec = spec;

    render();
  } catch (err) {
    console.error("[renderNotationSystemCue] failed:", err);
  }
}


/**
 * Record one element's connection-graph edges (follow()/oscRef bindings,
 * trig:<uid> and threshold trigs) and reference notes. Shared by the
 * assignCues walk and registerSingleCue, so a surgically re-registered
 * element keeps its cords in the patch view without a whole-score rescan.
 */
function collectGraphEdges(child, cueExpr, ast) {
  const _ownUid = String(
    ast.args?.find(a => a.type === "uid")?.value ?? ast.params?.uid ?? ast.uid ?? ""
  );
  // Most cues carry their parameters as an args LIST; video and notation
  // build a params OBJECT instead. Both can hold a follow(), so both are
  // walked — otherwise a whole cue type is invisible to the graph.
  const _bindables = [
    ...(ast.args || []).map((a) => [a?.type, a?.value]),
    ...Object.entries(ast.params || {}),
  ];
  for (const [param, v] of _bindables) {
    // mul(a, b) is one binding reading several sources. Each term gets
    // its own edge and its own reference check, so the patch view draws a
    // cord per term and a broken term is reported like any other — the
    // view already fans several cords into one inlet.
    if (v?.type === "combine") {
      for (const term of bindingSources(v)) {
        if (term.type === "signalRef") noteSignalRef(term, child, cueExpr, param);
        addSignalEdge(term, param, _ownUid, child, cueExpr);
      }
    } else if (v?.type === "signalRef") {
      noteSignalRef(v, child, cueExpr, param);
      addSignalEdge(v, param, _ownUid, child, cueExpr);
    } else if (v?.type === "oscRef") {
      addSignalEdge(v, param, _ownUid, child, cueExpr);
    }
  }
  const _trigArg = ast.args?.find(a => a.type === "trig")?.value ?? ast.params?.trig ?? ast.trig;
  if (isObjectTrig(_trigArg)) addTriggerEdge(String(_trigArg), _ownUid, child, cueExpr);
  // A threshold trig is a connection too: adc.onset fires this cue, and
  // the patch view should show that cord like any other.
  else if (isSignalTrig(_trigArg)) {
    const c = parseCondition(String(_trigArg));
    if (c?.signal) {
      const [src, ch] = String(c.signal).split(".");
      addSignalTriggerEdge(src, ch, _ownUid, child, cueExpr);
      noteSignalRef({ source: src, channel: ch }, child, cueExpr);
    }
  }
}

export async function assignCues(svgRoot, cuesArray = []) {
  // Cross-element references are collected during the walk and judged after
  // it — an element may name a uid that this same walk registers later.
  beginRefScan();
  beginEdgeScan();

  // How many times each cue expression has been seen in this walk, so repeats
  // can be told apart. See the push below.
  const _exprSeen = new Map();

  // Cues the array ALREADY holds, element → Set of expression texts: a repeat
  // scan into the same array (any caller passing window.cues twice) must not
  // append duplicates — twin entries share trigger state badly, and the
  // swelling list slows every frame until a reload.
  const _held = new Map();
  for (const c of cuesArray) {
    let s = _held.get(c.element);
    if (!s) _held.set(c.element, s = new Set());
    s.add(c.id);
  }

  // Collect async prime operations to await at the end
  const primePromises = [];

  function walk(node) {
    for (const child of node.children) {
      const id = child.id;
      const dataOscilla = child.getAttribute('data-oscilla');

      // --------------------------------------------------
      //  Fast reject: no cue source in either data-oscilla or id
      // --------------------------------------------------
      const dslSource = (dataOscilla && CUE_START_RE.test(dataOscilla)) ? dataOscilla
                      : (id && CUE_START_RE.test(id)) ? id
                      : null;
      if (!dslSource) {
        walk(child);
        continue;
      }

      // In-memory migration: if DSL lives in id (not yet in data-oscilla),
      // mirror it into data-oscilla now so the inspector reads it correctly.
      // The SVG file is NOT written here — migration to disk happens lazily
      // on the first browser edit via the DSL editor save path.
      if (dslSource === id && !dataOscilla) {
        child.setAttribute('data-oscilla', id);
      }

      // --------------------------------------------------
      // 🔹 Split compound cue IDs: "osc(...) scale(...)"
      // --------------------------------------------------
      const cueExprs = splitCueId(dslSource);

      if (!cueExprs.length) {
        walk(child);
        continue;
      }

      // A station or leg (map mode): its cues run when the route arrives
      // there or takes that leg (route.js), never by the playhead.
      if (cueExprs.some(e => /^(station|leg)\s*\(/.test(e))) {
        walk(child);
        continue;
      }

      for (const cueExpr of cueExprs) {
        //  Safety: prefix guard per expression
        if (!CUE_START_RE.test(cueExpr)) continue;
        // drag() is preProcessDrag's, with no grammar rule: parsing it only
        // logs a parse error per drag object per load
        if (/^drag\s*\(/.test(cueExpr)) continue;

        let ast;
        try {
          ast = parseCueToAST(cueExpr);
        } catch {
          //  Silent skip — not a cue, not an error
          continue;
        }

        if (!ast || ast.type === "cueButton") continue;

        // Bindings and object triggers become graph edges here — for every cue
        // type, before the branches below divert a cue off the playhead list.
        // Recorded whether or not anything is playing: a connection belongs to
        // the score, not to playback.
        collectGraphEdges(child, cueExpr, ast);

        // =========================================
        // oscCtrl is NOT a triggerable cue.
        // Register it immediately and DO NOT add
        // to window.cues.
        // =========================================
        if (ast.type === "oscCtrl") {
          try {
            handleOscCtrlCue(child, ast.args || [], { fromAssign: true });
          } catch (err) {
            console.error("[oscCtrl] failed to register:", err);
          }
          //  Do not push to cues list
          continue;   // no walk(child): the children loop descends once at its end
        }

        // oscOut() / display() are signal sinks: always on, registered at
        // load like oscCtrl, never on the playhead list.
        // url(): a link, live from load (url.js)
        if (ast.type === "cueUrl") {
          try { handleUrlCue(child, ast.args || []); }
          catch (err) { console.error("[url] failed to register:", err); }
          continue;   // no walk(child): the children loop descends once at its end
        }

        if (ast.type === "cueOscOut" || ast.type === "cueDisplay") {
          try {
            (ast.type === "cueOscOut" ? handleOscOutCue : handleDisplayCue)(child, ast.args || []);
          } catch (err) {
            console.error(`[${ast.type === "cueOscOut" ? "oscOut" : "display"}] failed to register:`, err);
          }
          continue;   // no walk(child): the children loop descends once at its end
        }

        // =========================================
        // pin() standalone cue — register at load.
        // Locks element to playhead when center aligns.
        // Same behavior as pin:N param on other cues.
        // =========================================
        if (ast.type === "cuePin") {
          if (ast.release) registerPinRelease(child, ast.release);
          else registerPin(child, ast.dur, ast);
          // Don't add to cues array — registration handles it
          continue;   // no walk(child): the children loop descends once at its end
        }

        // =========================================
        // notation() — static visual, rendered at load.
        // Renders staff/notes positioned at the cue element.
        // =========================================
        if (ast.type === "cueNotation") {
          renderNotationCue(child, ast);
          // notation(…, trig:<uid>): the host is a probe target whose notes
          // fire one by one as the object crosses them (probeTriggers expands
          // the host to its .notation-note children at hit time)
          const nTrig = ast.params?.trig ?? ast.args?.find(a => a.type === "trig")?.value;
          if (isObjectTrig(nTrig)) registerProbeTarget(String(nTrig), child, () => {});
          continue;   // no walk(child): the children loop descends once at its end
        }

        // =========================================
        // controlXY is a control-plane cue.
        // Register it immediately (no playhead semantics).
        // =========================================
        if (ast.type === "cueControlXY") {
          const activateControlXY = () => {
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                try {
                  handleControlXYCue(child, ast.args || [], { fromAssign: true });
                } catch (err) {
                  console.error("[controlXY] activation failed:", err);
                }
              });
            });
          };

          // Activate after DOM is fully ready
          if (document.readyState === "complete") {
            activateControlXY();
          } else {
            window.addEventListener("load", activateControlXY, { once: true });
          }

          // Skip adding to cues list - it's not playhead-triggered
          continue;   // no walk(child): the children loop descends once at its end
        }

        // =========================================
        // o2p with trig:touch is IMMEDIATELY active
        // Register it now, don't wait for playhead
        // =========================================
        if (ast.type === "cueO2P") {
          const trigArg = ast.args?.find(a => a.type === "trig");
          const isTouchMode = trigArg && String(trigArg.value).toLowerCase() === "touch";

          if (isTouchMode) {
            // Defer activation to next frame to ensure DOM is ready
            const activateTouchMode = () => {
              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  try {
                    handleO2PCue(child, ast.args || [], { fromAssign: true, fromCueTrigger: true });
                  } catch (err) {
                    console.error("[o2p trig:touch] activation failed:", err);
                  }
                });
              });
            };

            // Activate after DOM is fully ready
            if (document.readyState === "complete") {
              activateTouchMode();
            } else {
              window.addEventListener("load", activateTouchMode, { once: true });
            }

            // Skip adding to cues list - it's not playhead-triggered
            continue;   // no walk(child): the children loop descends once at its end
          }
        }

        // =========================================
        // text with trig:click/toggle/extent - register handlers
        // =========================================
        if (ast.type === "cueText") {
          const trigArg = ast.args?.find(a => a.type === "trig");
          const trigMode = trigArg ? String(trigArg.value).toLowerCase() : "playhead";

          if (trigMode === "click" || trigMode === "toggle" || trigMode === "extent") {
            const registerTextTrigger = async () => {
              try {
                const textModule = await import("./text.js");
                if (trigMode === "click") {
                  textModule.registerClickTrigger(child, ast);
                } else if (trigMode === "toggle") {
                  textModule.registerToggleTrigger(child, ast);
                } else if (trigMode === "extent") {
                  textModule.registerExtentTrigger(child, ast);
                }
              } catch (err) {
                console.error(`[text trig:${trigMode}] registration failed:`, err);
              }
            };

            // Register after DOM is ready
            if (document.readyState === "complete") {
              registerTextTrigger();
            } else {
              window.addEventListener("load", registerTextTrigger, { once: true });
            }

            // Skip adding to cues list - it's not playhead-triggered
            continue;   // no walk(child): the children loop descends once at its end
          } else {
            // Playhead-triggered: register for preview support (hold:true)
            const registerPlayheadText = async () => {
              try {
                const textModule = await import("./text.js");
                textModule.registerPlayheadTrigger(child, ast);
              } catch (err) {
                console.error("[text trig:playhead] registration failed:", err);
              }
            };

            if (document.readyState === "complete") {
              registerPlayheadText();
            } else {
              window.addEventListener("load", registerPlayheadText, { once: true });
            }
          }
        }

        // -----------------------------------
        // Pre-prime fade targets
        // -----------------------------------
        if (ast.type === "cueFade") {
          window._fadeCues.set(child, ast);
          primeFadeTargetFromAST(ast, child);
        }
        if (ast.type === "cueImage") {
          // A signal-bound index is a lookup table, not a playhead event — the
          // cue's own docs say so ("a folder stops being a slideshow... dur is
          // ignored while this is bound"). Wire it at ASSIGN, the way scale's
          // signal-bound path already is, so it responds however the performer
          // arrived: jumping to a rehearsal mark sets suppressCueTriggers, so
          // a cue that only binds when the playhead crosses it leaves the
          // picture frozen while its source moves underneath.
          const _idx = (ast.args || []).find(a => a?.type === "index")?.value;
          const _idxBound = _idx?.type === "signalRef" || _idx?.type === "oscRef";
          import("./image.js")
            .then(mod => (_idxBound ? mod.handleImageCue(ast, child) : mod.primeImage(ast, child)));
        }

        // -----------------------------------
        // Pre-prime synth overlays
        // -----------------------------------
        if (ast.type === "cueSynth") {
          primeSynthOverlay(ast, child);
        }

        // -----------------------------------
        // Pre-prime audio overlays
        // -----------------------------------
        if (ast.type === "cueAudio") {
          // Waveform defaults to "self" — suppress HTML overlay when active
          const wfParam = ast.waveform ?? ast.params?.waveform ?? "self";
          if (wfParam === "none" || wfParam === "0") {
            primeAudioOverlay(ast, child);
          }
          // Collect async prime operation for later await
          primePromises.push(primeWaveform(ast, child));
        }

        if (ast.type === "cueAudioPool") {
          primeAudioPoolOverlay(ast, child);
          // Collect async prime operation for later await
          primePromises.push(primePoolWaveform(ast, child));
        }

        if (ast.type === "cueAudioImpulse") {
          primeAudioImpulseOverlay(ast, child);
          // Collect async prime operation for later await
          primePromises.push(primeImpulseWaveform(ast, child));
        }

        // -----------------------------------
        // Click-to-trigger for audio/synth cues
        //
        // Enabled by default for all audio and synth cue types.
        // Disable with click:0 in the DSL.
        //
        // Uses pointerdown/pointerup with move threshold
        // to distinguish click from drag(1) gestures.
        // -----------------------------------
        // cueMidi with trig:click — set up pointer handler
        if (ast.type === "cueMidi") {
          const midiTrigArg = ast.args?.find(a => a.type === "trig");
          const midiTrigMode = midiTrigArg ? String(midiTrigArg.value).toLowerCase() : "playhead";
          if (midiTrigMode === "click" && !child._midiClickBound) {
            child._midiClickBound = true;
            child.style.cursor = "pointer";
            child.style.pointerEvents = "all";
            if (child.dataset) child.dataset.cueClickable = "1";
            let _downX = 0, _downY = 0;
            const MOVE_THRESHOLD = 6;
            child.addEventListener("pointerdown", (e) => { _downX = e.clientX; _downY = e.clientY; });
            child.addEventListener("pointerup", (e) => {
              if (Math.abs(e.clientX - _downX) < MOVE_THRESHOLD && Math.abs(e.clientY - _downY) < MOVE_THRESHOLD) {
                e.stopPropagation();
                handleCueTrigger(ast, false, true, child);
              }
            });
          }
        }

        const _isClickableAudio =
          ast.type === "cueAudio" ||
          ast.type === "cueAudioPool" ||
          ast.type === "cueAudioImpulse" ||
          ast.type === "cueSynth" ||
          ast.type === "cueVideo" ||
          ast.type === "cueImage";

        const clickParam = _audioCueParam(ast, "click");
        const clickDisabled = clickParam === 0 || clickParam === "0" || clickParam === false;

        if (_isClickableAudio && !clickDisabled) {
          // Refresh the AST every scan so re-parsed params take effect;
          // the gesture handlers read it at event time.
          child._audioCueAst = ast;
          bindAudioCueGestures(child);
        }
        if (_isClickableAudio) applyDslExtent(child, ast);

        // Positional shorthand speed markers — speed(2), speed(-1, loop:2) —
        // are handled exclusively by the position watcher in speed.js
        // (crossing-based, direction-aware, loop-counted). Registering them
        // as trigger cues too would double-apply and steal loop counts:
        // the screen-edge trigger fires slightly before the scalar playhead
        // reaches the marker. A dur: makes it a RAMP, which only the trigger
        // system can run — speed(1, dur:edge) is a trigger cue even in
        // shorthand form, and extractSpeedCues leaves it out of the marker
        // map with the same test. Value-form cues (speed(value:N, dur:S))
        // stay trigger-based.
        if (ast.type === "cueSpeed" && /^speed\(\s*-?[0-9.]/.test(cueExpr) && ast.dur == null) {
          continue;
        }

        // trig:<uid> — fired by another object passing over this one, not by
        // the transport playhead (probeTriggers.js). Stays off the playhead
        // list; the uid registry still knows it.
        const trigVal = midiTrigOf(ast, ast.args?.find(a => a.type === "trig")?.value ?? ast.params?.trig ?? ast.trig);
        if (trigVal === null) continue;     // a trig:midi with no midi: (said)

        // trig:"adc.onset>0.5" — fired by a signal crossing a threshold, on the
        // rising edge. Like trig:<uid> this stays off the playhead list; unlike
        // it, there is no element to watch, only a value on the bus. trig:midi
        // is one: the note's signal (midiTrigOf).
        if (isSignalTrig(trigVal)) {
          const onceVal = ast.args?.find(a => a.type === "once")?.value ?? ast.params?.once ?? ast.once;
          const once = onceVal === 1 || onceVal === "1" || onceVal === true;
          const step = patternStepper();          // its patterns step a hit (patternSteps.js)
          registerSignalTrigger(String(trigVal), () => handleCueTrigger(step(ast), false, true, child),
                                { key: child, once });
          continue;   // no walk(child): the children loop descends once at its end
        }

        // trig:manual / trig:none — started only by another cue (next:, a
        // button's trigger:), never by the playhead. zoom()/ui() with trig:auto
        // run once the project has loaded (animation.js), not by the playhead.
        // trig:click on a sound — played by clicking it (the gestures were
        // bound above), never by the playhead.
        const _trigLc = String(trigVal ?? "").toLowerCase();
        if (_trigLc === "manual" || _trigLc === "none" || ((ast.type === "cueZoom" || ast.type === "cueUi") && _trigLc === "auto")
            || (_trigLc === "click" && _AUDIO_GESTURE_TYPES.includes(ast.type))) {
          continue;   // no walk(child): the children loop descends once at its end
        }

        if (isObjectTrig(trigVal)) {
          const onceVal = ast.args?.find(a => a.type === "once")?.value ?? ast.params?.once ?? ast.once;
          const once = onceVal === 1 || onceVal === "1" || onceVal === true;
          registerProbeTarget(String(trigVal), child, () => handleCueTrigger(ast, false, true, child), { once });
          continue;   // no walk(child): the children loop descends once at its end
        }

        const rect = child.getBoundingClientRect();

        // window.triggeredCues is keyed by this, and cue.id is the DSL text —
        // so four elements carrying the SAME expression (a pack of images all
        // written `image(path:horse, uid:pack, mode:shuffle)`) would share one
        // entry and could not hold independent trigger state: one firing would
        // mark them all fired.
        //
        // The first occurrence keeps the bare expression, so the id stays
        // usable as the network identity for cue sync (socket.js). Only the
        // duplicates are suffixed.
        // Already registered for this element (a repeat scan): keep the
        // existing entry and its trigger state, register nothing twice.
        const _heldSet = _held.get(child);
        if (_heldSet?.has(cueExpr)) continue;

        const nth = (_exprSeen.get(cueExpr) ?? 0) + 1;
        _exprSeen.set(cueExpr, nth);

        // Where this cue LIVES in the score, in world units, captured now —
        // before any animation has moved anything. A trans() or o2p() element
        // travels under its own animation, so its live position is not where
        // the playhead should measure it from. See the scrub-back test.
        let homeWorldX = null;
        {
          const _svg = child.ownerSVGElement;
          const _ctm = _svg?.getScreenCTM?.();
          if (_ctm && _ctm.a !== 0) homeWorldX = (rect.left - _ctm.e) / _ctm.a;
        }

        cuesArray.push({
          id: cueExpr,
          key: nth === 1 ? cueExpr : `${cueExpr}#${nth}`,
          homeWorldX,
          ast,
          element: child,
          triggered: false
        });
        if (_heldSet) _heldSet.add(cueExpr);
        else _held.set(child, new Set([cueExpr]));


        // Generic pin: any cue with pin:N gets pinned to playhead
        const pinDur = ast.params?.pin ?? ast.pin ?? ast.args?.find(a => a.type === 'pin')?.value;
        if (pinDur != null && pinDur > 0) {
          registerPin(child, Number(pinDur));
        }
      }

      // Continue walking down — the ONLY descent into this child. The cue-type
      // branches above end in a bare `continue`: a walk(child) of their own
      // would descend a second time here and register every cue in the
      // subtree twice.
      walk(child);
    }
  }

  walk(svgRoot);

  // --------------------------------------------------------
  // BUILD CUE BUTTONS & CLICK OVERLAYS
  // --------------------------------------------------------
  // Which container the overlays belong in follows from the ROOT being
  // assigned, not from a global set the last time a score was initialised.
  // A page renders through renderPageContent, which calls assignCues directly
  // and never touches window.isPageOverlay — so for a page the global still
  // holds the score's value, and these would build the page's buttons into
  // #scoreInner, underneath the page overlay (z-index 25000), destroying the
  // correctly-placed ones page.js has just made.
  //
  // Same test initializeSVG uses to set the global (SVGInit.js).
  const rootIsPage =
    svgRoot?.id === "pageSVG" ||
    svgRoot?.classList?.contains?.("oscilla-page") === true;

  if (rootIsPage || window.isPageOverlay) {
    buildCueButtonsIn(svgRoot, svgRoot);
    buildClickOverlaysIn(svgRoot, svgRoot);
    buildDragClickOverlaysIn(svgRoot, svgRoot);
    buildDragOverlaysIn(svgRoot, svgRoot);
  } else {
    const scrollContainer = document.getElementById("scoreInner");
    if (scrollContainer) {
      buildCueButtonsIn(svgRoot, scrollContainer);
      buildClickOverlaysIn(svgRoot, scrollContainer);
      buildDragClickOverlaysIn(svgRoot, scrollContainer);
      buildDragOverlaysIn(svgRoot, scrollContainer);
    }
  }

  // Reposition click overlays after drag overlays restore saved positions
  // Use timeout to ensure transforms have been applied
  setTimeout(() => {
    repositionAllClickOverlays();
  }, 100);

  // Reposition o2p launcher circles after drag overlays restore saved positions
  // and after launchers are created (they use 300ms timeout + import delay)
  // Use multiple repositions to handle timing variations
  const repositionLaunchers = () => {
    import('../control/o2pLauncher.js').then(({ repositionAllLaunchers }) => {
      repositionAllLaunchers();
    });
  };
  // Reposition at various times to ensure circles are placed correctly
  setTimeout(repositionLaunchers, 500);
  setTimeout(repositionLaunchers, 1000);
  // Also trigger on load complete
  if (document.readyState === 'complete') {
    setTimeout(repositionLaunchers, 100);
  } else {
    window.addEventListener('load', () => setTimeout(repositionLaunchers, 100));
  }

  // Wait for all async prime operations (audio waveforms, etc.)
  // This ensures the progress indicator knows about pending audio downloads
  if (primePromises.length > 0) {
    await Promise.all(primePromises);
  }

  // Everything named during the walk is resolvable by now, or it never will be.
  reportUnresolvedRefs();
  return cuesArray;
}

/**
 * The cue state a move of the playhead resets (relocatePlayhead in
 * oscillaTransport.js), on the client that moved it and on each client that
 * follows its jump, so all of them leave the new place with the same cues
 * to come:
 *  - the crossing bookkeeping from scratch, and the playhead's file voices
 *    and impulses stopped (resetCueEdgeTracking)
 *  - tapes an external engine plays stopped: they belong to the place left
 *  - the fades still ahead primed again (every fade, with allFades: a rewind)
 *  - the stopwatch overlays taken down
 *  - nav() repeat counts cleared, unless the jump IS a nav() repeat
 *    (keepNavRepeats): clearing them there restarts the count, and
 *    nav(scroll@mark, repeats:N) loops for ever
 */
export function resetCueState({ keepNavRepeats = false, allFades = false } = {}) {
  resetCueEdgeTracking();
  for (const [k, v] of window.activeAudioCues || []) {
    if (!v?.external) continue;
    try { v.stop(0.05); } catch { /* already stopped */ }
    window.activeAudioCues.delete(k);
  }
  resetAllFadePriming({ all: allFades });
  dismissAllStopwatchOverlays();
  if (!keepNavRepeats) window.navRepeatMap?.clear();
}

// -------------------- Cue Utilities --------------------
/**
 * checkCueTriggers()
 *
 * Called on each animation frame to evaluate whether the scrolling playhead intersects
 * with any cue elements. Triggers cue actions using `handleCueTrigger()`.
 *
 * Supports:
 * - Single-shot cue triggering (via `triggeredCues`)
 * - Cooldown suppression after jump events to avoid double triggers
 */

// A live pin's current horizontal shift in world units, read once per frame
// per wrapper (the wrapper's translate is in its parent's units, which for a
// layer-level pin are world units).
function livePinOffset(wrapper, frameNo) {
  if (wrapper._pinFrame === frameNo) return wrapper._pinOffset;
  let off = 0;
  const list = wrapper.transform?.baseVal;
  if (list && list.numberOfItems) off = list.consolidate()?.matrix?.e || 0;
  wrapper._pinFrame = frameNo;
  wrapper._pinOffset = off;
  return off;
}

// ── onscreen ─────────────────────────────────────────────────────────────
// A cue fires only if its element is on screen at that moment when it asks
// for it (onscreen:1), or when this client's "Trigger only on screen"
// preference is on. onscreen:0 opts a cue out of the preference. The
// preference leaves score-control cues alone — a pause, zoom or page turn has
// to happen wherever its marker is. Checked only at the moment of firing, so
// it costs nothing per frame. A cue held back counts as fired for that pass.
const ONSCREEN_EXEMPT = new Set([
  "cuePause", "cueZoom", "cueNav", "cuePage", "cueStop", "cueSpeed",
  "cueStopwatch", "cueMetronome", "cueUi",
]);
try {
  if (window.triggerOnScreenOnly == null) {
    window.triggerOnScreenOnly = localStorage.getItem("oscilla.triggerOnScreenOnly") === "1";
  }
} catch { /* no storage */ }

function onscreenWanted(ast) {
  const v = ast?.args?.find?.(a => a.type === "onscreen")?.value ?? ast?.params?.onscreen ?? ast?.onscreen;
  if (truthy(v)) return true;
  if (isOff(v)) return false;
  return !!window.triggerOnScreenOnly && !ONSCREEN_EXEMPT.has(ast?.type);
}

function isOnScreen(el) {
  const r = el?.getBoundingClientRect?.();
  if (!r || (!r.width && !r.height)) return false;          // hidden (display:none)
  const c = (window.scoreContainer || document.getElementById("scoreContainer"))?.getBoundingClientRect?.();
  if (!c) return true;
  return r.right >= c.left && r.left <= c.right && r.bottom >= c.top && r.top <= c.bottom;
}

function fireCue(cue) {
  if (onscreenWanted(cue.ast) && !isOnScreen(cue.element)) return false;
  handleCueTrigger(cue.ast, false, true, cue.element);
  return true;
}

window.evaluateCueIntersections = true;

/**
 * Fire OSC for each notation note whose host has playhead-pass triggers
 * enabled (DSL `playhead:1`). Uses left→right edge-crossing so each note
 * fires once per pass; reverses reset the side back to 'left' so a fresh
 * forward pass triggers it again. Called from inside checkCueTriggers().
 */
function checkNotationNoteTriggers(playheadX) {
  const containerRect = window.scoreContainer?.getBoundingClientRect();
  if (!containerRect) return;
  const hosts = document.querySelectorAll("[data-notation-uid]");
  if (!hosts.length) return;

  hosts.forEach(host => {
    const g = host._notationGroup;
    if (!g) return;
    const noteEls = g.querySelectorAll(".notation-note");
    noteEls.forEach((noteEl) => {
      if (!noteEl._notationPlayheadEnabled) return;
      const payload = noteEl._notationOscPayload;
      if (!payload) return;
      const rect = noteEl.getBoundingClientRect();
      // Note centre x in container-relative screen coords
      const cx = (rect.left + rect.right) / 2 - containerRect.left;
      const wasLeft = noteEl._notationLastSide !== "right";
      const isRight = cx <= playheadX;
      if (wasLeft && isRight) {
        noteEl._notationOscFire?.(payload.index);
        // Phase 7e: also fire any gliss-source OSC payloads attached to
        // this note (one fire per connected gliss originating here).
        if (noteEl._notationGlissFires) {
          for (const fire of noteEl._notationGlissFires) {
            try { fire(); } catch (err) { console.warn("[notation gliss osc] fire failed:", err); }
          }
        }
        noteEl._notationLastSide = "right";
      } else if (!isRight && noteEl._notationLastSide === "right") {
        noteEl._notationLastSide = "left";
      }
    });
  });
}

// How often a static cue's geometry is re-measured live (see the loop)
const CACHE_REFRESH_FRAMES = 20;
let _frameNo = 0, _staggerSeed = 0;

export async function checkCueTriggers() {
  // Global cue suppression guard (jumping, scrubbing, loading, etc.)
  if (window.suppressCueTriggers) return;
  _frameNo++;

  // TELEPORT MODE: Skip cue checks entirely during server sync jumps
  // This prevents cue cascade when clients catch up to server time
  if (window._isTeleporting) return;

  // Ensure cues are ready
  if (!Array.isArray(window.cues)) return;

  // Sync elapsed time based on current scroll position
  setElapsed(elapsedAtPlayhead());

  //  Skip cue checks if paused, seeking, or not playing

  if (window.isSeeking || !window.evaluateCueIntersections) return;

  const playheadX = playheadScreenX();   // screen px, as the cue rects below
  if (playheadX === null) {
    console.warn("[checkCueTriggers] Could not determine playhead x position.");
    return;
  }

  // Persistent per-cue state
  if (!window._prevCueLefts) window._prevCueLefts = new Map();
  if (!window._prevCueTrailing) window._prevCueTrailing = new Map();
  if (!window._cueInsideState) window._cueInsideState = new Map();
  if (!window.triggeredCues) window.triggeredCues = new Set();

  // Notation per-note playhead-pass triggers (phase 6b). Fires OSC for any
  // notation note whose host opted in via playhead:1 in the DSL.
  checkNotationNoteTriggers(playheadX);

  const tolerance = 8; // px

  const containerRect =
    window.scoreContainer.getBoundingClientRect();

  const isVerticalMode = window.scrollMode === "vertical";

  // Score→screen transform, read once per frame: the scrub-back test needs it
  // to place a cue's HOME position, not the position its own animation has
  // taken it to.
  const _svgRoot = document.querySelector("#scoreContainer svg") || document.querySelector("svg");
  const _rootCtm = (!isVerticalMode && _svgRoot?.getScreenCTM) ? _svgRoot.getScreenCTM() : null;

  // Viewport culling distance along the scroll axis
  const cullDistance = isVerticalMode
    ? window.innerHeight * 1.5
    : window.innerWidth * 1.5;

  for (const cue of window.cues) {
    if (!cue?.element) continue;

    // Per-cue state — triggered, previous edges, inside/outside — is keyed by
    // this, NOT by cue.id. cue.id is the DSL text, so several elements carrying
    // the same expression would share one set of state: their previous
    // positions would overwrite each other every frame, and the crossing test
    // would fire spuriously.
    const _cueKey = cue.key ?? cue.id;

    // SPATIAL CULLING: Skip cues far from playhead using cached world coordinate.
    // Only active in horizontal mode — in vertical mode the cached X coords are
    // on the wrong axis so we skip the optimisation (vertical scores rarely have
    // thousands of cues where it matters).
    // Material under a live pin (pin(live:1)) is measured where the hold puts
    // it: its cached world position is shifted by how far the pin has moved
    // since the cache was taken (pinShift below).
    if (cue._livePin === undefined) cue._livePin = cue.element.closest?.("g.pin-wrapper[data-pin-live]") || null;
    const _pinShift = cue._livePin ? livePinOffset(cue._livePin, _frameNo) - (cue._cachedPinOffset || 0) : 0;

    if (!isVerticalMode && cue._cachedWorldX != null) {
      // One getScreenCTM per frame, not one per cue: every cue of the score
      // shares the root <svg>. A cue inside a nested <svg> still gets its own.
      const svg = cue.element.ownerSVGElement;
      const ctm = svg === _svgRoot ? _rootCtm : svg?.getScreenCTM();
      if (ctm) {
        const screenX = ctm.a * (cue._cachedWorldX + _pinShift) + ctm.e - containerRect.left;
        // to the nearest part of the cue, not its left edge: a region wider
        // than the cull window (a tape under the playhead for 16 s) with the
        // playhead deep in its right half was culled -- never entered, never
        // left, silent after a jump into it
        const screenW = Math.abs(ctm.a * (cue._cachedWorldW || 0));
        let distance = playheadX < screenX ? screenX - playheadX
          : playheadX > screenX + screenW ? playheadX - (screenX + screenW) : 0;
        // Guard against a poisoned cache: if the cached position ever ends up
        // far from the authored one (e.g. it was refreshed while a pin hold
        // displaced the element), culling by it alone would hide the cue
        // exactly when the playhead passes its real position — and a culled
        // cue can never refresh its way back. The authored home keeps it in.
        if (cue.homeWorldX != null) {
          const homeScreenX = ctm.a * cue.homeWorldX + ctm.e - containerRect.left;
          distance = Math.min(distance, Math.abs(homeScreenX - playheadX));
        }
        if (distance > cullDistance) {
          continue; // Skip this cue - too far from playhead
        }
      }
    }

    // Use parent group's bbox if element is inside a group (for SVG-authored extent lines)
    // Only for cue types that use extent regions (audio, audioPool, audioImpulse, synth)
    // NOT for simple cues like stop, pause, nav, fade, etc.
    let cueEl = cue.element;
    const extentCueTypes = ["cueAudio", "cueAudioPool", "cueAudioImpulse", "cueSynth"];
    const isExtentCue = extentCueTypes.includes(cue.ast?.type);
    if (isExtentCue) {
      // A cue hosted on a <g> (DSL-editor wrapper, or authored group) already
      // defines its own extent — promoting to the parent would adopt the bbox
      // of an arbitrary enclosing group, making the region absurdly large and
      // the enter/exit trigger unreachable.
      const isGroupHost = cueEl.tagName?.toLowerCase() === 'g';
      // A DSL extent:N fully defines the region — never promote to the
      // parent group (which may be an arbitrary large container)
      const hasDslExtent = cueEl._dslExtentW > 0;
      const parent = cueEl.parentElement;
      if (!isGroupHost && !hasDslExtent && isAuthoredExtentGroup(parent, cueEl)) {
        cueEl = parent;
      }
    }

    // An element under its own animation is measured where it LIVES, not
    // where the animation has taken it -- the rule the scrub-back test below
    // already applies -- so its edges come from the world geometry cached
    // when it was first seen, placed with this frame's root CTM, saving a
    // getBoundingClientRect per animating element per frame. Static elements
    // keep the live rect, so a dragged object still fires where it was put.
    //
    // A static element is measured live only every CACHE_REFRESH_FRAMES
    // frames (staggered, so the calls spread evenly), and from its cached
    // geometry otherwise -- the score scrolls and zooms through the CTM, so
    // the cache only goes stale when the element itself is moved, and then
    // by at most a third of a second. A score's cues are mostly static and
    // mostly within the cull window (1.5 screens), so a live rect for each of
    // them every frame would dominate the frame.
    let cueLeft, cueRight;
    const _cacheable = !isVerticalMode && cueEl === cue.element && _rootCtm
      && cue._cachedWorldX != null && cue._cachedWorldW != null
      && cue.element.ownerSVGElement === _svgRoot;
    const _animating = _cacheable && isElementAnimating(cue.element);
    if (cue._stagger === undefined) cue._stagger = (_staggerSeed++) % CACHE_REFRESH_FRAMES;
    let _useCache = _cacheable && (_animating || (_frameNo + cue._stagger) % CACHE_REFRESH_FRAMES !== 0);
    // A pin hold displaces the element without its own animation running: a
    // live rect then measures where the PIN carried it, not where it lives,
    // and a cache refreshed from it strands the cue's trigger up to the
    // whole hold's width downstream — where the cull test hides it, so no
    // later refresh ever heals it. Pin-held material measures from cache, exactly like an animating
    // element. Checked only on would-be refresh frames, so the closest()
    // walk stays off the hot path.
    // An element carried by a moving ANCESTOR — a star on a rotating sky, a
    // member of a group on an o2p — is "static" to the test above, yet it
    // moves between refreshes: its cached position lags, then snaps, and a
    // small element can snap clean over the playhead without a frame inside
    // it, so it never fires. Near the playhead such an element is measured
    // live every frame; further away the cache is fine (it only culls).
    if (_useCache && !_animating) {
      if (cue._inMover === undefined) {
        cue._inMover = !!cue.element.parentElement?.closest?.(
          '[data-oscilla*="rotate("], [data-oscilla*="o2p("], [data-oscilla*="trans("], [data-oscilla*="scale("]');
      }
      if (cue._inMover) {
        const near = _rootCtm.a * (cue._cachedWorldX + _pinShift) + _rootCtm.e - containerRect.left;
        if (Math.abs(near - playheadX) < MOVER_LIVE_PX + Math.abs(_rootCtm.a * cue._cachedWorldW)) _useCache = false;
      }
    }
    if (_cacheable && !_useCache && !cue._livePin && cue.element.closest?.("g.pin-wrapper[transform]")) _useCache = true;
    if (_useCache) {
      cueLeft  = _rootCtm.a * (cue._cachedWorldX + _pinShift) + _rootCtm.e - containerRect.left;
      cueRight = cueLeft + _rootCtm.a * cue._cachedWorldW;
    } else {
      const cueRect = cueEl.getBoundingClientRect();
      // In vertical mode the scroll axis is Y, so leading/trailing edges are top/bottom.
      // We reuse cueLeft/cueRight variable names to keep all downstream logic unchanged.
      if (isVerticalMode) {
        cueLeft  = cueRect.top  - containerRect.top;
        cueRight = cueLeft + cueRect.height;
      } else {
        cueLeft  = cueRect.left - containerRect.left;
        cueRight = cueLeft + cueRect.width;
        // a fresh measurement of a static element refreshes its home geometry
        // (never from an animating one: its rect includes its own transform)
        if (_cacheable && !_animating && _rootCtm.a !== 0) {
          cue._cachedWorldX = (cueRect.left - _rootCtm.e) / _rootCtm.a;
          cue._cachedWorldW = cueRect.width / _rootCtm.a;
          if (cue._livePin) cue._cachedPinOffset = livePinOffset(cue._livePin, _frameNo);
        }
      }
    }

    // Cache world X (and width) for horizontal-mode culling and for the home
    // geometry above (only on first calculation, not in vertical). Use the
    // cue's OWN element position — cueLeft may be the promoted parent group's
    // edge, which for large groups sits far from the element and would
    // permanently cull the cue.
    if (!isVerticalMode && cue._cachedWorldX == null
        && (cue._livePin || !cue.element.closest?.("g.pin-wrapper[transform]"))) {
      const svg = cue.element.ownerSVGElement;
      if (svg) {
        const ctm = svg === _svgRoot ? _rootCtm : svg.getScreenCTM();
        if (ctm && ctm.a !== 0) {
          const ownRect = cue.element.getBoundingClientRect();
          cue._cachedWorldX = (ownRect.left - ctm.e) / ctm.a;
          cue._cachedWorldW = ownRect.width / ctm.a;
          if (cue._livePin) cue._cachedPinOffset = livePinOffset(cue._livePin, _frameNo);
        }
      }
    }

    // DSL extent:N — boundary computed live from the element's current
    // bbox, so it tracks scrolling, zooming, and element drags.
    if (!isVerticalMode && cue.element._dslExtentW > 0) {
      const scale = cue.element.parentElement?.getScreenCTM?.()?.a;
      if (scale) {
        const elLeft = cue.element.getBoundingClientRect().left - containerRect.left;
        cueLeft  = elLeft;
        cueRight = elLeft + cue.element._dslExtentW * scale;
      }
    }

    // Where the cue was last frame is kept RELATIVE TO THE PLAYHEAD LINE
    // and read back against this frame's line: a crossing is the cue going
    // from ahead of the line to behind it, whichever of the two moved. In
    // scroll mode it is the score that moves -- the same test as before --
    // but zoom(..., playhead:stay, hold:...) keeps the view still and slides
    // the line instead, and measured on the screen a cue the line passed
    // had never moved: zoom(home:1) never fired and the hold never ended.
    const prevLeft = window._prevCueLefts.has(_cueKey)
      ? window._prevCueLefts.get(_cueKey) + playheadX
      : undefined;

    const prevInside = window._cueInsideState.get(_cueKey) || false;
    const isInside = playheadX >= cueLeft && playheadX <= cueRight;

    // 🔧 update state FIRST
    window._cueInsideState.set(_cueKey, isInside);

    // ======================================================
    // 🔊 OSC RE-ENTRANT PLAYHEAD TRIGGER
    // ======================================================
    if (cue.ast?.type === "cueOsc") {

      // First encounter: if already inside, don't trigger
      if (cue._armed === undefined) {
        cue._armed = true;
        if (isInside) {
          // We loaded/reset with playhead inside - don't auto-fire
          cue._armed = false;
          window._cueInsideState.set(_cueKey, true);
        }
      }
      if (cue._lastOscFire === undefined) cue._lastOscFire = 0;

      const now = performance.now();
      const COOLDOWN = 80;

      // ENTER
      if (!prevInside && isInside && cue._armed) {
        if (now - cue._lastOscFire >= COOLDOWN) {
          fireCue(cue);
          cue._armed = false;
          cue._lastOscFire = now;
        }
      }

      // EXIT
      if (prevInside && !isInside) {
        cue._armed = true;
      }
    }

    // ======================================================
    // 🎹 MIDI RE-ENTRANT PLAYHEAD TRIGGER
    // ======================================================
    if (cue.ast?.type === "cueMidi") {
      if (cue._armed === undefined) {
        cue._armed = true;
        if (isInside) {
          cue._armed = false;
          window._cueInsideState.set(_cueKey, true);
        }
      }
      if (cue._lastMidiFire === undefined) cue._lastMidiFire = 0;

      const now = performance.now();
      const COOLDOWN = 80;

      if (!prevInside && isInside && cue._armed) {
        if (now - cue._lastMidiFire >= COOLDOWN) {
          fireCue(cue);
          cue._armed = false;
          cue._lastMidiFire = now;
        }
      }

      if (prevInside && !isInside) {
        cue._armed = true;
      }
    }

    // ======================================================
    // 🎹 SYNTH RE-ENTRANT PLAYHEAD TRIGGER
    // Same enter/exit/re-arm pattern as cueOsc above.
    // Prevents auto-firing on page load when playhead starts
    // inside the synth element. EXIT stops the synth and
    // re-arms for next entry.
    // ======================================================
    if (cue.ast?.type === "cueSynth") {
      // First encounter: if already inside on page load, don't trigger
      if (cue._armed === undefined) {
        cue._armed = true;
        if (isInside) {
          // We loaded with playhead inside - don't auto-fire
          cue._armed = false;
          window._cueInsideState.set(_cueKey, true);
          window.triggeredCues.add(_cueKey);
        }
      }

      // ENTER -- fire the synth
      if (!prevInside && isInside && cue._armed) {
        fireCue(cue);
        cue._armed = false;
        window.triggeredCues.add(_cueKey);
      }

      // EXIT -- stop synth and re-arm
      if (prevInside && !isInside) {
        cue._armed = true;
        window.triggeredCues.delete(_cueKey);
        const uid = _cueSynthUid(cue.ast, cue.element);
        if (uid) stopSynth(uid);
      }
    }

    // ======================================================
    // 🔊 AUDIO RE-ENTRANT PLAYHEAD TRIGGER
    // Same enter/exit/re-arm pattern as cueOsc above.
    // Allows drag-in / drag-out / drag-in workflows with
    // drag(1) elements, and normal transport triggering.
    //
    // For cueAudio (drone/loop), EXIT also stops the
    // active voice so the element behaves like a physical
    // instrument: drag in = play, drag out = stop.
    //
    // For cueAudioPool and cueAudioImpulse, EXIT only
    // re-arms — voices play to natural completion.
    // ======================================================
    const _audioReentrantType = cue.ast?.type;
    const _isAudioReentrant =
      _audioReentrantType === "cueAudioPool" ||
      _audioReentrantType === "cueAudioImpulse" ||
      _audioReentrantType === "cueAudio";

    if (_isAudioReentrant) {
      // First encounter: if already inside on page load, don't trigger
      // but mark state so future exit/enter works correctly.
      // Except a tape an external engine plays (audio(engine:external)):
      // it JOINS -- starts at the point the playhead is at, so pressing play
      // inside it, or jumping into it, plays it from there. Met while the
      // transport is stopped, it waits (_armed stays undefined) and joins
      // when playback starts.
      const _joins = isInside && _audioReentrantType === "cueAudio"
        && /^(external|ext)$/i.test(String(cue.ast?.params?.engine ?? cue.ast?.engine ?? "").trim());
      if (cue._armed === undefined && _joins) {
        if (transport.isPlaying) {
          fireCue(cue);
          cue._armed = false;
          window.triggeredCues.add(_cueKey);
          window._cueInsideState.set(_cueKey, true);
        }
      } else if (cue._armed === undefined) {
        cue._armed = true;
        if (isInside) {
          // We loaded with playhead inside - don't auto-fire
          // Mark as already triggered so it doesn't fire until exit/re-enter
          cue._armed = false;
          window.triggeredCues.add(_cueKey);
          window._cueInsideState.set(_cueKey, true);
        }
      }

      // ENTER -- fire the cue (only on actual transition from outside to inside)
      if (!prevInside && isInside && cue._armed) {
        fireCue(cue);
        cue._armed = false;
        // Mark in triggeredCues for consistency
        window.triggeredCues.add(_cueKey);
      }

      // EXIT -- re-arm so next entry fires again
      if (prevInside && !isInside) {
        cue._armed = true;
        window.triggeredCues.delete(_cueKey);

        // Cancel any pending spread-poly setTimeout voices for this cue element.
        // Without this, voices 2..N of a poly spread fire after exit and play forever.
        if (cue.element) {
          cue.element._spreadGen = (cue.element._spreadGen || 0) + 1;
        }

        // For audioFile: stop active voice(s) on exit with fadeOut.
        // Uses the fadeOut from the DSL params, falling back to 0.3s.
        // uid fallback matches audioFile.js: uid ?? params.uid ?? src
        if (_audioReentrantType === "cueAudio" || _audioReentrantType === "cueAudioPool") {
          const uid = cue.ast?.uid ?? cue.ast?.params?.uid ?? cue.ast?.src ?? cue.ast?.params?.path;
          if (uid) {
            const baseUid = uid.trim();
            const fadeOut = secs(cue.ast?.fadeOut ?? cue.ast?.params?.fadeOut ?? 0.3);
            const reg = window.activeAudioCues;
            if (reg) {
              for (const [k, voice] of reg.entries()) {
                if (k === baseUid || k.startsWith(`${baseUid}__`)) {
                  try { voice?.stop?.(fadeOut); } catch (err) { console.warn("[CueDSL] voice stop failed:", err); }
                  reg.delete(k);
                  window.dispatchEvent(new CustomEvent("oscilla:audio", {
                    detail: { uid: k, file: voice?.filename || "", state: "stop" }
                  }));
                }
              }
            }
          }
        }

        // For audioImpulse: stop the process and remove from Map
        // so re-entry creates a fresh process instead of hot-updating
        // a dead one (timer cleared, stopped=true).
        if (_audioReentrantType === "cueAudioImpulse") {
          const uid = cue.ast?.uid ?? cue.ast?.params?.uid ?? cue.ast?.src ?? cue.ast?.params?.path;
          if (uid) {
            const fadeOut = secs(cue.ast?.fadeOut ?? cue.ast?.params?.fadeOut ?? 0.15);
            stopAudioImpulse(uid.trim(), fadeOut);
          }
        }
      }
    }

    // The "leading edge" is the side of the cue that crosses the playhead first.
    // In normal (LTR/TTB) mode it's the left/top edge (cueLeft).
    // In reversed (RTL/BTU) mode the score moves in the opposite direction so
    // the right/bottom edge (cueRight) arrives at the playhead first.
    const leadingEdge = transport.scrollReversed ? cueRight : cueLeft;

    // Initialise state on first encounter
    if (prevLeft === undefined) {
      window._prevCueLefts.set(_cueKey, leadingEdge - playheadX);
      window._prevCueTrailing.set(_cueKey, (transport.scrollReversed ? cueLeft : cueRight) - playheadX);
      window._cueInsideState.set(_cueKey, isInside);
      continue;
    }

    // Detect forward motion: in normal mode elements move toward lower screen
    // coords; in reversed mode they move toward higher screen coords.
    const movingForward = transport.scrollReversed
      ? leadingEdge > prevLeft
      : leadingEdge < prevLeft;

    const crossedLeftEdgeForward = transport.scrollReversed
      ? movingForward && prevLeft < (playheadX - tolerance) && leadingEdge >= (playheadX - tolerance)
      : movingForward && prevLeft > (playheadX + tolerance) && leadingEdge <= (playheadX + tolerance);

    const isRepeatNavCue =
      cue.ast?.type === "cueNav" &&
      cue.ast?.params &&
      cue.ast.params.repeats !== undefined;

    // ======================================================
    // 🎯 PRIMARY TRIGGER
    // Skip for audio/synth reentrant cues - they use their own ENTER logic above
    // Skip for click:1 or drag-click:1 cues - those are click-only, not playhead triggered
    // ======================================================
    const _isSynthCue = cue.ast?.type === "cueSynth";
    const _isClickOnly = cue.ast?.args?.some(a =>
      (a.type === "click" || a.type === "drag-click") &&
      (a.value === 1 || a.value === true || a.value === "1")
    ) || (
      // video(trig:click/manual) — click-triggered only, skip playhead firing
      cue.ast?.type === "cueVideo" &&
      ["click", "manual"].includes(String(cue.ast?.params?.trig ?? "").toLowerCase())
    );
    const _isOscCue = cue.ast?.type === "cueOsc";
    const _isMidiCue = cue.ast?.type === "cueMidi";
    // Animation cues with trig:auto are self-running (started at load by
    // animationAssign) — the playhead must not restart them mid-flight.
    // Playhead-synced (re)starts are what trig:playhead says explicitly.
    const _ANIM_CUE_TYPES = ["cueScale", "cueRotate", "cueTrans", "cueO2P", "cueColor", "cueFade"];
    const _isAnimCue = _ANIM_CUE_TYPES.includes(cue.ast?.type);
    const _animTrig = String(cue.ast?.args?.find?.(a => a.type === "trig")?.value ?? "").toLowerCase();
    const _isAutoAnim = _isAnimCue && _animTrig === "auto";
    // Started by the playhead, so the playhead may also unwind it. Anything
    // driven by a hand (trig:touch, trig:click) or by another object
    // (trig:<uid>) is left alone.
    const _byPlayhead = _animTrig === "" || _animTrig === "playhead";
    const _isPlayheadAnim = _isAnimCue && _byPlayhead;
    // image() unwinds the same way: a slideshow should not keep dealing frames
    // behind a playhead that has not reached it
    const _isPlayheadImage = cue.ast?.type === "cueImage" && _byPlayhead;
    if (crossedLeftEdgeForward && !_isAudioReentrant && !_isSynthCue && !_isClickOnly && !_isOscCue && !_isMidiCue && !_isAutoAnim) {
      if (!isRepeatNavCue && window.triggeredCues.has(_cueKey)) {
        // already fired → skip
      } else {
        fireCue(cue);

        if (!isRepeatNavCue) {
          window.triggeredCues.add(_cueKey);
        }
      }
    }

    // ======================================================
    // ◀ SCRUBBED BACK BEHIND AN ANIMATION
    // The playhead has ended up before a trig:playhead animation that has
    // already fired — a rewind, a seek, a jump to a mark. The score should
    // look as it did before that object played, so stop it, put it back to
    // its start position and re-arm the cue for the next forward pass.
    // Without this a rotate() carries on spinning behind a playhead that has
    // not reached it yet, and never fires again because it is still marked
    // as triggered.
    //
    // trig:auto animations are exempt on purpose: they are not the playhead's
    // to start, so they are not the playhead's to stop.
    // ======================================================
    //
    // What counts as "this already played" is the object's own running state,
    // NOT window.triggeredCues. Every move of the playhead resets that set
    // (resetCueState), so it is empty by the time we get here and nothing
    // would ever be reset.
    const _stillRunning = _isPlayheadImage
      ? cue.element?._oscillaImageRunning === true
      : (_isPlayheadAnim && isElementAnimating(cue.element));
    if ((_isPlayheadAnim || _isPlayheadImage) && !_isClickOnly &&
        (_stillRunning || window.triggeredCues.has(_cueKey))) {
      // Measured from where the cue LIVES, not where it currently is.
      //
      // A trans() or o2p() element travels under its own animation. By its
      // live position, travelling away from the playhead would look like the
      // playhead had been scrubbed back — the animation would be reset, the
      // element snap home, cross the playhead, fire again, and travel off
      // again, in a loop.
      //
      // Home is captured at assign time, before anything has moved. For an
      // element that never moves it is the same number as leadingEdge.
      const homeEdge = (cue.homeWorldX != null && _rootCtm && _rootCtm.a !== 0)
        ? _rootCtm.a * cue.homeWorldX + _rootCtm.e - containerRect.left
        : leadingEdge;
      const cueIsAhead = transport.scrollReversed
        ? homeEdge < (playheadX - tolerance)
        : homeEdge > (playheadX + tolerance);
      if (cueIsAhead) {
        window.triggeredCues.delete(_cueKey);
        if (_stillRunning) {
          if (_isPlayheadImage) {
            // stops the slideshow and restores the placeholder
            import("./image.js").then(mod => mod.resetImage(cue.element, cue.ast));
          } else {
            resetAnimationsForElement(cue.element);
          }
        }
      }
    }

    // ======================================================
    // ◀ REVERSE-DIRECTION TRIGGER (opt-in via revtrig:1 / revtrig:2)
    // With speed(-N) the score moves backwards; by default cues only
    // fire on forward crossings. A cue carrying revtrig also fires on
    // reverse crossings:
    //   revtrig:1 — at its TRAILING edge (right edge in normal mode):
    //               the entry edge when travelling backwards, mirroring
    //               the forward trigger's left-edge entry
    //   revtrig:2 — at its LEADING edge (left edge): the exact same
    //               score location as the forward trigger
    // Firing in reverse re-arms the forward trigger, so ping-pong
    // passes over the cue keep firing it.
    // ======================================================
    const _revTrigVal = cue.ast?.params?.revtrig ?? cue.ast?.revtrig
      ?? cue.ast?.args?.find?.(a => a.type === "revtrig")?.value;
    const _revTrigMode = _revTrigVal === true ? 1 : (Number(_revTrigVal) || 0);
    // Trailing edge (the side opposite leadingEdge) — tracked for revtrig:1
    const trailingEdge = transport.scrollReversed ? cueLeft : cueRight;
    const _prevT = window._prevCueTrailing.get(_cueKey);
    const prevTrailing = _prevT === undefined ? undefined : _prevT + playheadX;
    if (_revTrigMode && !_isAudioReentrant && !_isSynthCue && !_isClickOnly && !_isOscCue && !_isMidiCue && !_isAutoAnim) {
      const movingBackward = transport.scrollReversed
        ? leadingEdge < prevLeft
        : leadingEdge > prevLeft;
      let crossedReverse;
      if (_revTrigMode === 2) {
        // left/leading edge — same spot the forward trigger fires at
        crossedReverse = transport.scrollReversed
          ? movingBackward && prevLeft > (playheadX + tolerance) && leadingEdge <= (playheadX + tolerance)
          : movingBackward && prevLeft < (playheadX - tolerance) && leadingEdge >= (playheadX - tolerance);
      } else {
        // right/trailing edge — first edge the reversing playhead meets
        crossedReverse = prevTrailing !== undefined && (transport.scrollReversed
          ? movingBackward && prevTrailing > (playheadX + tolerance) && trailingEdge <= (playheadX + tolerance)
          : movingBackward && prevTrailing < (playheadX - tolerance) && trailingEdge >= (playheadX - tolerance));
      }
      if (crossedReverse) {
        fireCue(cue);
        // Re-arm the forward trigger for the next forward pass
        window.triggeredCues.delete(_cueKey);
      }
    }

    // Update state for next frame
    window._prevCueTrailing.set(_cueKey, trailingEdge - playheadX);
    window._prevCueLefts.set(_cueKey, leadingEdge - playheadX);
    window._cueInsideState.set(_cueKey, isInside);
  }
}

export function resetCueEdgeTracking() {
  const RESET_FADE = 0.15;

  // Stop any audio that was playing from cues
  // Use activeAudioCues registry directly since _cueInsideState may already be cleared
  if (Array.isArray(window.cues)) {
    for (const cue of window.cues) {
      const astType = cue.ast?.type;

      if (astType === "cueAudio" || astType === "cueAudioPool") {
        const uid = cue.ast?.uid ?? cue.ast?.params?.uid ?? cue.ast?.src ?? cue.ast?.params?.path;
        if (uid) {
          const baseUid = uid.trim();
          const reg = window.activeAudioCues;
          if (reg) {
            for (const [k, voice] of reg.entries()) {
              if (k === baseUid || k.startsWith(`${baseUid}__`)) {
                try { voice?.stop?.(RESET_FADE); } catch (err) { console.warn("[CueDSL] voice stop failed:", err); }
                reg.delete(k);
              }
            }
          }
        }
      }

      if (astType === "cueAudioImpulse") {
        const uid = cue.ast?.uid ?? cue.ast?.params?.uid ?? cue.ast?.src ?? cue.ast?.params?.path;
        if (uid) {
          stopAudioImpulse(uid.trim(), RESET_FADE);
        }
      }
    }
  }

  window._prevCueLefts = new Map();
  window._prevCueTrailing = new Map();
  window._cueInsideState = new Map();
  window.triggeredCues = new Set();

  // Mark cues as needing re-initialization on next frame
  // This prevents false triggers when landing inside a cue after reset
  if (Array.isArray(window.cues)) {
    for (const cue of window.cues) {
      // Set _armed to undefined so first-encounter logic runs
      // This properly handles the case where we land inside a cue
      cue._armed = undefined;
    }
  }
}
window.resetCueEdgeTracking = resetCueEdgeTracking;

/* ============================================================================
 *  Delayed start (tdelay:) for the self-timed animation cues
 *  -------------------------------------------------
 *  rotate, scale, colour and o2p read their own tdelay: (cfg.start, seconds
 *  after secs()) and start through scheduleCueStart. The wait is a step of
 *  the sequence (sequence.js later()), as the dispatcher's own tdelay: is:
 *  the sequence button holds it, and a rewind, still() or a project switch
 *  (sequence stop()) drops it.
 *
 *    • cfg.start 0 or absent starts at once.
 *    • A second start of the same uid replaces the one still waiting.
 *    • window.pendingCueStarts : Map(uid → { timeoutId, cfg, el, startFn }),
 *      the starts still waiting, for the console:
 *      console.table([...window.pendingCueStarts.keys()])
 * ============================================================================
 */
window.pendingCueStarts ??= new Map();

/**
 * cfg.start         = delay in seconds (optional, 0 = immediate)
 * cfg._armed        = true when init:armed was parsed
 * cfg._startBlocked = true until performer clicks
 *
 * When armed: schedules the fade to ghost opacity but does NOT
 * start the animation. Animation starts when performer clicks.
 */
export function scheduleCueStart(cfg, el, startFn, uid) {
  const delay = Number(cfg.start ?? 0);

  // Cancel previous pending start for this uid
  if (window.pendingCueStarts.has(uid)) {
    cancelSequenceLater(window.pendingCueStarts.get(uid).timeoutId);
    window.pendingCueStarts.delete(uid);
  }

  // armed: the prestate (a fade to ghost opacity) and no start
  const begin = () => {
    if (cfg._armed && cfg._startBlocked) {
      cfg._applyPrestateOnStart?.();
      return;
    }
    startFn();
  };

  if (!delay || delay <= 0) { begin(); return; }

  const timeoutId = sequenceLater(() => {
    if (window.pendingCueStarts.get(uid)?.timeoutId === timeoutId) window.pendingCueStarts.delete(uid);
    begin();
  }, delay * 1000);
  window.pendingCueStarts.set(uid, { timeoutId, cfg, el, startFn, createdAt: performance.now() });
}